import React, { useEffect, useMemo, useState } from 'react';
import { formatReminderSchedule, formatReminderMeta } from '../utils/reminderFormat';
import {
  formatCallHeading,
  formatAttemptLabel,
  formatOutcome,
  formatWhen,
  nonEngagementBanner,
} from '../utils/companionCallHistory';
import { stateForDisclaimer, DISCLAIMER_TEXT } from '../utils/reminderDisclaimer';
import { PetsEditor, FaithEditor, WorkingLifeEditor, ChildhoodEditor } from '../components/IntakeFieldEditors';
import { splitChipInput } from '../utils/chipSplit';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { relationOptions, relationLabel } from '../i18n/relations';
import { QRCodeSVG } from 'qrcode.react';
import { useFavicon } from '../utils/useFavicon';
import TimezoneSelect from '../components/TimezoneSelect';

const VERSION = '1.26.1';
// #711 — derive from the current page's host instead of hardcoding
// production, so the QR code + copy-link on staging point to the
// staging tablet bundle. Same logic as IntakeReview.js.
const TABLET_URL = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? `${window.location.origin}/app`
  : 'https://amigomio.app/app';

const SECTIONS = [
  { key: 'profile',    icon: '👤', active: true,  path: '/portal/profile' },
  { key: 'interests',  icon: '🎯', active: true,  path: '/portal/interests' },
  { key: 'family',     icon: '👨‍👩‍👧', active: true,  path: '/portal/family' },
  { key: 'friends',    icon: '🤝', active: true,  path: '/portal/friends' },
  // #711 — four new optional intake topics surfaced as Portal tiles.
  // Each tile renders a lightweight read-only summary of what's stored
  // and links the family to /intake/review for full editing — that's
  // where the structured PetsEditor + freetext fields live (see
  // IntakeReview.js + the matching extraction in app/api/intake.py).
  { key: 'pets',       icon: '🐾', active: true,  path: '/portal/pets' },
  { key: 'faith',      icon: '🕊️', active: true,  path: '/portal/faith' },
  { key: 'childhood',  icon: '📖', active: true,  path: '/portal/childhood' },
  { key: 'work',       icon: '💼', active: true,  path: '/portal/work' },
  { key: 'health',     icon: '🏥', active: true,  path: '/portal/health' },
  { key: 'alerts',     icon: '🔔', active: true,  path: '/portal/alerts' },
  { key: 'photos',     icon: '🖼️', active: true,  path: '/portal/photos' },
  { key: 'reminders',  icon: '⏰', active: true,  path: '/portal/reminders' },
  { key: 'calls',      icon: '📞', active: true,  path: '/portal/calls' },
  { key: 'gifts',      icon: '🎁', active: true,  path: '/portal/gifts' },
  { key: 'wellbeing',  icon: '💙', active: true,  path: '/portal/wellbeing' },
  { key: 'companion', icon: '🤖', active: true,  path: '/portal/companion' },
  { key: 'social',     icon: '👨‍👩‍👧', active: true,  path: '/portal/social' },
  // #244 — family-user account self-service (profile edit, password,
  // trial status, self-delete). The nav entry sits at the bottom
  // because it's about the family account, not the elderly user.
  { key: 'account',    icon: '⚙️', active: true,  path: '/portal/account' },
  // #245 — "Music (coming soon)" nav item removed; we're not building a
  // dedicated music section. Berta's [PLAY_MUSIC] capability stays.
  // News briefing moved to admin user detail — removed from portal nav
  // Conversation activity removed — monitoring belongs in admin, not family portal
];

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: #FAF8F5;
    color: #2C2C2C;
    -webkit-font-smoothing: antialiased;
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Header ── */
  .portal-header {
    background: #fff;
    border-bottom: 1px solid rgba(0,0,0,0.07);
    padding: 0 40px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 50;
  }
  .portal-logo {
    /* #238 PR-2 — icon mark + wordmark text. Replaces the 🎵 emoji. */
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 1.1rem;
    font-weight: 700;
    color: #2C2C2C;
    letter-spacing: -0.02em;
  }
  .portal-logo img { height: 28px; width: auto; display: block; }
  .portal-logo .ver { font-size: 0.7rem; color: #aaa; font-weight: 400; }
  .portal-user {
    display: flex;
    align-items: center;
    gap: 20px;
  }
  .portal-username {
    font-size: 0.9rem;
    font-weight: 500;
    color: #444;
  }
  .btn-logout {
    background: none;
    border: 1.5px solid #E0DDD8;
    border-radius: 20px;
    padding: 7px 18px;
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
  }
  .btn-logout:hover { border-color: #2C2C2C; color: #2C2C2C; }

  /* ── Main ── */
  .portal-main {
    max-width: 960px;
    margin: 0 auto;
    padding: 48px 24px 80px;
    animation: fadeInUp 0.5s ease-out both;
  }

  /* ── Intake banner ── */
  .intake-banner {
    background: linear-gradient(135deg, #E8845A 0%, #d9724a 100%);
    border-radius: 16px;
    padding: 32px 36px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 48px;
    box-shadow: 0 4px 24px rgba(232,132,90,0.3);
  }
  .intake-text h2 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #fff;
    margin-bottom: 6px;
  }
  .intake-text p {
    font-size: 0.9rem;
    color: rgba(255,255,255,0.82);
    line-height: 1.5;
  }
  .btn-intake {
    background: #fff;
    color: #E8845A;
    font-family: inherit;
    font-size: 0.95rem;
    font-weight: 700;
    padding: 14px 28px;
    border-radius: 50px;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .btn-intake:hover {
    transform: scale(1.03);
    box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  }

  /* ── Section grid ── */
  .sections-heading {
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.10em;
    text-transform: uppercase;
    color: #999;
    margin-bottom: 20px;
  }
  .sections-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
  }
  .section-card {
    background: #fff;
    border-radius: 14px;
    padding: 28px 24px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    text-decoration: none;
    display: block;
    border: 1.5px solid transparent;
    transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
    cursor: pointer;
  }
  .section-card.active:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 24px rgba(0,0,0,0.10);
    border-color: rgba(232,132,90,0.3);
  }
  .section-card.inactive {
    opacity: 0.55;
    cursor: default;
    pointer-events: none;
  }
  .card-icon {
    font-size: 1.75rem;
    margin-bottom: 14px;
    display: block;
  }
  .card-title {
    font-size: 1rem;
    font-weight: 700;
    color: #1A1A1A;
    margin-bottom: 6px;
  }
  .card-desc {
    font-size: 0.85rem;
    color: #777;
    line-height: 1.5;
  }
  .coming-soon-badge {
    display: inline-block;
    margin-top: 10px;
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #bbb;
    background: #F0EDEA;
    padding: 3px 10px;
    border-radius: 20px;
  }

  /* ── Detail view ── */
  .detail-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #888;
    text-decoration: none;
    margin-bottom: 24px;
    transition: color 0.2s;
  }
  .detail-back:hover { color: #2C2C2C; }
  .detail-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1A1A1A;
    margin-bottom: 28px;
  }
  .detail-card {
    background: #fff;
    border-radius: 14px;
    padding: 28px 32px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    margin-bottom: 16px;
  }
  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 14px 0;
    border-bottom: 1px solid #F0EDEA;
  }
  .detail-row:last-child { border-bottom: none; }
  .detail-label {
    font-size: 0.82rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #999;
    flex-shrink: 0;
  }
  .detail-value {
    font-size: 0.95rem;
    color: #2C2C2C;
    text-align: right;
  }
  .chip-list { display: flex; flex-wrap: wrap; gap: 8px; justify-content: flex-end; }
  .chip {
    background: #F3EDE7;
    color: #5A4E42;
    font-size: 0.82rem;
    font-weight: 500;
    padding: 6px 14px;
    border-radius: 20px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .chip-delete {
    background: none;
    border: none;
    color: #A09080;
    font-size: 0.85rem;
    cursor: pointer;
    padding: 0 2px;
    line-height: 1;
    transition: color 0.2s;
  }
  .chip-delete:hover { color: #DC3545; }
  /* #168 — structured interests: 8 labelled chip lists, one per category. */
  .structured-interests { display: flex; flex-direction: column; gap: 22px; }
  .interest-field { padding: 14px 0; border-bottom: 1px solid #ECE6DE; }
  .interest-field:last-child { border-bottom: none; }
  .interest-field-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #8A7B6A;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0 0 10px 0;
  }
  .interest-field-empty { color: #B5A998; margin: 4px 0 0 0; font-size: 0.85rem; }
  /* "auto" badge for conversation-extracted entries — small, muted so
     family-curated chips still read as the primary content. */
  .chip-source-badge {
    background: #E8845A;
    color: #fff;
    font-size: 0.62rem;
    font-weight: 700;
    padding: 1px 6px;
    border-radius: 8px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .add-interest-row {
    display: flex;
    gap: 8px;
    margin-top: 12px;
  }
  .add-interest-input {
    flex: 1;
    border: 1.5px solid #E0D8D0;
    border-radius: 10px;
    padding: 8px 14px;
    font-size: 0.9rem;
    font-family: inherit;
    color: #2C2C2C;
    background: #FAFAF8;
    outline: none;
    transition: border-color 0.2s;
  }
  .add-interest-input:focus { border-color: #E8845A; background: #fff; }
  .add-interest-btn {
    background: #E8845A;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 8px 18px;
    font-size: 0.85rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.2s;
  }
  .add-interest-btn:hover { background: #D4724A; }
  .add-interest-btn:disabled { background: #ccc; cursor: not-allowed; }
  .save-feedback {
    font-size: 0.8rem;
    margin-top: 10px;
    font-weight: 500;
    animation: fadeInUp 0.3s ease;
  }
  .save-feedback.success { color: #28A745; }
  .save-feedback.error { color: #DC3545; }
  .btn-edit-section {
    background: none;
    border: 1.5px solid #E0DDD8;
    border-radius: 8px;
    padding: 6px 16px;
    font-family: inherit;
    font-size: 0.82rem;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
  }
  .btn-edit-section:hover { border-color: #E8845A; color: #E8845A; }
  .btn-save-section {
    background: #E8845A;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 6px 16px;
    font-family: inherit;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
  }
  .btn-save-section:hover { background: #D4724A; }
  .btn-save-section:disabled { background: #ccc; cursor: not-allowed; }
  .btn-cancel-section {
    background: none;
    border: 1.5px solid #E0DDD8;
    border-radius: 8px;
    padding: 6px 16px;
    font-family: inherit;
    font-size: 0.82rem;
    font-weight: 500;
    color: #666;
    cursor: pointer;
  }
  .edit-input {
    border: 1.5px solid #E0D8D0;
    border-radius: 8px;
    padding: 6px 12px;
    font-size: 0.9rem;
    font-family: inherit;
    color: #2C2C2C;
    background: #FAFAF8;
    outline: none;
    width: 100%;
    max-width: 280px;
    transition: border-color 0.2s;
  }
  .edit-input:focus { border-color: #E8845A; background: #fff; }
  .edit-select {
    border: 1.5px solid #E0D8D0;
    border-radius: 8px;
    padding: 6px 12px;
    font-size: 0.85rem;
    font-family: inherit;
    color: #2C2C2C;
    background: #FAFAF8;
    outline: none;
  }
  .family-row-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
  }
  .btn-icon {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 0.85rem;
    padding: 4px 6px;
    border-radius: 6px;
    transition: background 0.15s, color 0.15s;
    color: #999;
  }
  .btn-icon:hover { background: #F0EDEA; color: #2C2C2C; }
  .btn-icon.danger:hover { background: #FEE; color: #DC3545; }
  .confirm-delete {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.82rem;
    color: #DC3545;
  }
  .confirm-delete button {
    font-family: inherit;
    font-size: 0.78rem;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 6px;
    cursor: pointer;
    border: none;
  }
  .confirm-yes { background: #DC3545; color: #fff; }
  .confirm-no { background: #F0EDEA; color: #666; }
  .family-list { list-style: none; padding: 0; }
  .family-list li {
    padding: 12px 0;
    border-bottom: 1px solid #F0EDEA;
    font-size: 0.95rem;
    color: #2C2C2C;
  }
  .family-list li:last-child { border-bottom: none; }
  .sensitive-note {
    font-size: 0.75rem;
    color: #bbb;
    font-style: italic;
    margin-top: 12px;
  }
  .empty-state {
    color: #999;
    font-size: 0.9rem;
    font-style: italic;
  }
  .no-intake-banner {
    text-align: center;
    padding: 48px 24px;
  }
  .no-intake-banner h3 {
    font-size: 1.15rem;
    font-weight: 700;
    color: #2C2C2C;
    margin-bottom: 8px;
  }
  .no-intake-banner p {
    font-size: 0.9rem;
    color: #777;
    margin-bottom: 20px;
  }
  .loading-spinner {
    text-align: center;
    padding: 48px;
    color: #999;
    font-size: 0.9rem;
  }

  /* ── Setup card ── */
  .setup-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: #fff;
    border-radius: 16px;
    padding: 32px 36px;
    margin-bottom: 48px;
    box-shadow: 0 2px 16px rgba(0,0,0,0.06);
    text-align: center;
    border: 1.5px solid #E8F5E9;
  }
  .setup-badge {
    display: block;
    width: 100%;
    font-size: 0.85rem;
    font-weight: 700;
    color: #28A745;
  }
  .btn-copy-setup {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    background: #E8845A;
    color: #fff;
    font-family: inherit;
    font-size: 0.95rem;
    font-weight: 600;
    padding: 12px 28px;
    border-radius: 50px;
    border: none;
    cursor: pointer;
    transition: background 0.2s, transform 0.15s;
  }
  .btn-copy-setup:hover { background: #D4724A; transform: scale(1.03); }
  .btn-copy-setup.copied { background: #28A745; cursor: default; }
  .setup-qr { }
  .setup-hint {
    font-size: 0.8rem;
    color: #999;
    line-height: 1.5;
  }

  /* ── Photos section ── */
  .photos-upload-area {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    flex-wrap: wrap;
    margin-bottom: 24px;
  }
  .photos-upload-btn {
    background: #E8845A;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-size: 0.85rem;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.2s;
  }
  .photos-upload-btn:hover { background: #D4724A; }
  .photos-upload-btn:disabled { background: #ccc; cursor: not-allowed; }
  .photos-fields {
    display: flex;
    gap: 8px;
    flex: 1;
    min-width: 200px;
  }
  .photos-fields input {
    flex: 1;
    border: 1.5px solid #E0D8D0;
    border-radius: 10px;
    padding: 8px 14px;
    font-size: 0.85rem;
    font-family: inherit;
    color: #2C2C2C;
    background: #FAFAF8;
    outline: none;
    transition: border-color 0.2s;
  }
  .photos-fields input:focus { border-color: #E8845A; background: #fff; }
  .photos-preview {
    margin-bottom: 16px;
    text-align: center;
  }
  .photos-preview img {
    max-width: 200px;
    max-height: 160px;
    border-radius: 10px;
    object-fit: cover;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }
  .photos-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
  }
  .photo-card {
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .photo-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0,0,0,0.10);
  }
  .photo-card img {
    width: 100%;
    height: 140px;
    object-fit: cover;
    display: block;
  }
  .photo-card-info {
    padding: 10px 12px;
  }
  .photo-card-desc {
    font-size: 0.82rem;
    color: #2C2C2C;
    margin-bottom: 4px;
    word-break: break-word;
  }
  .photo-card-date {
    font-size: 0.75rem;
    color: #999;
  }
  .photo-card-delete {
    background: none;
    border: none;
    color: #bbb;
    font-size: 0.75rem;
    cursor: pointer;
    float: right;
    transition: color 0.2s;
  }
  .photo-card-delete:hover { color: #DC3545; }

  /* ── Alerts ── */
  .alerts-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
  }
  .alert-badge {
    background: #DC3545;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 12px;
    min-width: 20px;
    text-align: center;
  }
  .alert-card {
    background: #fff;
    border-radius: 14px;
    padding: 24px 28px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    margin-bottom: 12px;
    border-left: 4px solid #E8845A;
  }
  .alert-card.seen {
    border-left-color: #ccc;
    opacity: 0.7;
  }
  .alert-date {
    font-size: 0.78rem;
    color: #999;
    margin-bottom: 8px;
  }
  .alert-message {
    font-size: 0.95rem;
    color: #2C2C2C;
    line-height: 1.6;
    margin-bottom: 12px;
  }
  .alert-crisis-line {
    font-size: 0.85rem;
    color: #666;
    background: #FFF8F5;
    border-radius: 8px;
    padding: 10px 14px;
    margin-bottom: 12px;
  }
  .btn-mark-seen {
    background: none;
    border: 1.5px solid #E0DDD8;
    border-radius: 8px;
    padding: 6px 16px;
    font-family: inherit;
    font-size: 0.82rem;
    font-weight: 500;
    color: #666;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
  }
  .btn-mark-seen:hover { border-color: #2C2C2C; color: #2C2C2C; }

  @media (max-width: 640px) {
    .portal-header { padding: 0 20px; }
    .intake-banner { flex-direction: column; align-items: flex-start; }
    .portal-main { padding: 32px 16px 60px; }
    .detail-row { flex-direction: column; gap: 4px; }
    .detail-value { text-align: left; }
    .chip-list { justify-content: flex-start; }
  }
`;

/* ── Section detail components ── */

// #711 — Editable section wrapper for the four new intake topics. View
// mode shows the captured data summary; "Edit" toggles into an inline
// editor (the same PetsEditor / FaithEditor / WorkingLifeEditor +
// childhood-textarea components used by IntakeReview). Save POSTs only
// the touched field via PUT /api/intake/review — the backend handler
// is field-scoped (`if "pets" in d:` etc.) so other sections aren't
// disturbed. On success, the local data prop is updated optimistically
// via onDataChange so the view re-renders immediately without a full
// refetch.
function IntakeFieldSummarySection({
  data, onDataChange, label, icon, blurb,
  valueRenderer, renderItem, emptyState, extraNote = null,
  field, Editor, defaultEditValue,
  // #711 — for sections whose editor manipulates more than one
  // top-level data key (Childhood = childhood + childhood_sensitive_notes),
  // override how the draft maps onto the PUT payload + onto the
  // optimistic local refresh. Both default to `{ [field]: draft }`.
  saveTransform = null,
  optimisticPatch = null,
  // For sections where the visual "edit" mode draft is derived from
  // multiple top-level keys (Childhood again), override how the draft
  // is initialised when entering edit mode. Default = currentValue or
  // defaultEditValue.
  draftInitializer = null,
}) {
  const items = (valueRenderer && valueRenderer(data)) || [];
  const empty = items.length === 0;
  const inlineEditable = Boolean(field && Editor);

  const currentValue = data && data.data ? data.data[field] : undefined;
  // Snapshot is what the editor edits. Initialised on entering edit mode
  // from currentValue so a Cancel reverts cleanly.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const startEdit = () => {
    if (draftInitializer) {
      setDraft(draftInitializer(data));
    } else {
      setDraft(currentValue !== undefined && currentValue !== null && currentValue !== ''
        ? currentValue
        : defaultEditValue);
    }
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setDraft(undefined);
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    const token = localStorage.getItem('am_token');
    const payload = saveTransform ? saveTransform(draft) : { [field]: draft };
    const patch = optimisticPatch ? optimisticPatch(draft) : payload;
    try {
      const res = await fetch('/api/intake/review', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          user_id: data ? data.user_id : null,
          data: payload,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      setFeedback({ type: 'success', msg: 'Saved' });
      setEditing(false);
      // Optimistic local refresh so the view-mode summary repaints
      // straight away. The next page load will re-fetch from the server
      // and confirm.
      if (onDataChange && data) {
        onDataChange({
          ...data,
          data: { ...(data.data || {}), ...patch },
        });
      }
    } catch {
      setFeedback({ type: 'error', msg: 'Could not save — please try again' });
    }
    setSaving(false);
    setTimeout(() => setFeedback(null), 2500);
  };

  // Delete = Save with the empty-default value. The PUT handler's
  // delete-then-insert pattern naturally clears the memory rows when
  // the supplied value has no fillable fields.
  const handleDelete = async () => {
    if (!window.confirm(`Remove all ${label.toLowerCase()} information?`)) return;
    setSaving(true);
    setFeedback(null);
    const token = localStorage.getItem('am_token');
    const payload = saveTransform ? saveTransform(defaultEditValue) : { [field]: defaultEditValue };
    const patch = optimisticPatch ? optimisticPatch(defaultEditValue) : payload;
    try {
      const res = await fetch('/api/intake/review', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          user_id: data ? data.user_id : null,
          data: payload,
        }),
      });
      if (!res.ok) throw new Error('Delete failed');
      setFeedback({ type: 'success', msg: 'Removed' });
      if (onDataChange && data) {
        onDataChange({
          ...data,
          data: { ...(data.data || {}), ...patch },
        });
      }
    } catch {
      setFeedback({ type: 'error', msg: 'Could not remove — please try again' });
    }
    setSaving(false);
    setTimeout(() => setFeedback(null), 2500);
  };

  return (
    <>
      <div
        style={{
          background: '#FFF8F0',
          border: '1px solid #F0E0D0',
          borderRadius: 10,
          padding: '10px 16px',
          marginBottom: 16,
          fontSize: '0.85rem',
          color: '#8A7050',
          lineHeight: 1.5,
        }}
      >
        {icon && <span style={{ marginRight: 8 }}>{icon}</span>}
        {blurb}
      </div>
      <div className="detail-card">
        {editing && inlineEditable ? (
          <>
            <Editor value={draft} onChange={setDraft} />
            <div style={{ display: 'flex', gap: 8, marginTop: 16, alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                style={{
                  background: '#E8845A', color: 'white', border: 'none',
                  padding: '8px 16px', borderRadius: 6,
                  fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit',
                  opacity: saving ? 0.6 : 1,
                }}
              >{saving ? 'Saving…' : 'Save'}</button>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                style={{
                  background: 'transparent', color: '#666',
                  border: '1px solid #DDD',
                  padding: '8px 16px', borderRadius: 6,
                  fontSize: '0.88rem', fontWeight: 500, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >Cancel</button>
              {feedback && (
                <span style={{
                  fontSize: '0.85rem',
                  color: feedback.type === 'success' ? '#1a8a4a' : '#c0392b',
                  marginLeft: 8,
                }}>{feedback.msg}</span>
              )}
            </div>
          </>
        ) : (
          <>
            {empty ? (
              <p style={{ color: '#999', fontStyle: 'italic', fontSize: '0.9rem', margin: '0 0 12px' }}>
                {emptyState}
              </p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 8, fontSize: '0.95rem' }}>
                {items.map((item, i) => renderItem(item, i))}
              </ul>
            )}
            {extraNote && extraNote(data)}
            <div style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {inlineEditable ? (
                <>
                  <button
                    type="button"
                    onClick={startEdit}
                    style={{
                      background: '#E8845A', color: 'white', border: 'none',
                      padding: '8px 16px', borderRadius: 6,
                      fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >{empty ? `+ Add ${label.toLowerCase()}` : 'Edit'}</button>
                  {!empty && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={saving}
                      style={{
                        background: 'transparent', color: '#c0392b',
                        border: '1px solid #E5C5C3',
                        padding: '8px 16px', borderRadius: 6,
                        fontSize: '0.88rem', fontWeight: 500, cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >Remove all</button>
                  )}
                </>
              ) : (
                // Fallback for sections without an inline editor — keep
                // the original "Edit in Intake Review" link.
                <Link
                  to="/intake/review"
                  style={{
                    display: 'inline-block',
                    background: '#E8845A',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: 6,
                    textDecoration: 'none',
                    fontSize: '0.88rem',
                    fontWeight: 600,
                  }}
                >
                  {empty ? `+ Add ${label.toLowerCase()}` : 'Edit in Intake Review'}
                </Link>
              )}
              {feedback && (
                <span style={{
                  fontSize: '0.85rem',
                  color: feedback.type === 'success' ? '#1a8a4a' : '#c0392b',
                  marginLeft: 8,
                }}>{feedback.msg}</span>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function ProfileSection({ data, t, onDataChange }) {
  const d = data.data || {};
  const nicknames = Array.isArray(d.nicknames) ? d.nicknames : [];
  const age = d.birth_year ? new Date().getFullYear() - Number(d.birth_year) : null;
  // #734 — full ISO birthday for display + edit. Falls back to year-
  // only legacy data (no birthday memory yet) so existing users keep
  // showing the age + year tuple in the read-only view.
  const birthdayIso = (typeof d.birthday === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d.birthday))
    ? d.birthday : '';

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: d.name || d.first_name || '',
    last_name: d.last_name || '',
    preferred_name: d.preferred_name || (nicknames.length > 0 ? nicknames[0] : ''),
    birth_year: d.birth_year || '',
    birthday: birthdayIso,
    location: d.location || '',
    language: data.language || 'en',
  });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Sync form when data prop changes (e.g. after section refetch)
  useEffect(() => {
    const d2 = data.data || {};
    const nicks = Array.isArray(d2.nicknames) ? d2.nicknames : [];
    const bday2 = (typeof d2.birthday === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d2.birthday))
      ? d2.birthday : '';
    setForm(prev => ({
      ...prev,
      name: d2.name || d2.first_name || prev.name,
      last_name: d2.last_name || prev.last_name,
      preferred_name: d2.preferred_name || (nicks.length > 0 ? nicks[0] : prev.preferred_name),
      birth_year: d2.birth_year || prev.birth_year,
      birthday: bday2 || prev.birthday,
      location: d2.location || prev.location,
      language: data.language || prev.language || 'en',
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.language]);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    const token = localStorage.getItem('am_token');
    try {
      // #734 — when the family edits the date, keep birth_year in
      // lockstep with the birthday's year part (so the age display +
      // era-appropriate music selection don't go stale on save).
      const derivedYear = (/^\d{4}-\d{2}-\d{2}$/.test(form.birthday || ''))
        ? String(form.birthday).slice(0, 4)
        : form.birth_year;
      const res = await fetch('/api/intake/review', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          user_id: data.user_id,
          data: {
            first_name: form.name,
            last_name: form.last_name,
            preferred_name: form.preferred_name,
            birth_year: derivedYear,
            birthday: form.birthday || '',
            location: form.location,
            language: form.language,
            nicknames: form.preferred_name ? [form.preferred_name] : [],
          },
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      setFeedback({ type: 'success', msg: 'Saved' });
      setEditing(false);
      if (onDataChange) {
        onDataChange({
          ...data,
          language: form.language,
          data: {
            ...d,
            name: form.name,
            first_name: form.name,
            last_name: form.last_name,
            preferred_name: form.preferred_name,
            birth_year: derivedYear,
            birthday: form.birthday || '',
            location: form.location,
            nicknames: form.preferred_name ? [form.preferred_name] : [],
          },
        });
      }
    } catch {
      setFeedback({ type: 'error', msg: 'Could not save' });
    }
    setSaving(false);
    setTimeout(() => setFeedback(null), 2500);
  };

  if (editing) {
    return (
      <div className="detail-card">
        <div className="detail-row">
          <span className="detail-label">{t.name}</span>
          <input className="edit-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="First name" />
        </div>
        <div className="detail-row">
          <span className="detail-label">Last name</span>
          <input className="edit-input" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" />
        </div>
        <div className="detail-row">
          <span className="detail-label">{t.nickname || 'Nickname'}</span>
          <input className="edit-input" value={form.preferred_name} onChange={e => setForm({ ...form, preferred_name: e.target.value })} placeholder="Preferred name" />
        </div>
        <div className="detail-row">
          {/* #734 — full birthday capture replaces year-only entry.
              Saving with a date here auto-creates a yearly conversation-
              mode reminder so Lena wishes them happy birthday on the day. */}
          <span className="detail-label">{t.birthday || 'Birthday'}</span>
          <input
            className="edit-input"
            type="date"
            value={form.birthday || ''}
            onChange={e => setForm({ ...form, birthday: e.target.value })}
            max={(() => { const d = new Date(); d.setFullYear(d.getFullYear() - 18); return d.toISOString().slice(0, 10); })() /* #741 — 18+ floor matches validateRequired */}
            min="1900-01-01"
          />
        </div>
        <div className="detail-row">
          <span className="detail-label">{t.location || 'Location'}</span>
          <input className="edit-input" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} />
        </div>
        <div className="detail-row">
          <span className="detail-label">{t.language || 'Language'}</span>
          {/* #735 — full list of supported product languages.
              Mirrors app/services/language.py:LANG_NAMES. Pre-fix
              was missing it + pt. */}
          <select className="edit-select" value={form.language} onChange={e => setForm({ ...form, language: e.target.value })}>
            <option value="en">English</option>
            <option value="nl">Nederlands</option>
            <option value="de">Deutsch</option>
            <option value="fr">Français</option>
            <option value="es">Español</option>
            <option value="it">Italiano</option>
            <option value="pt">Português</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button className="btn-save-section" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button className="btn-cancel-section" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
        </div>
        {feedback && <p className={`save-feedback ${feedback.type}`}>{feedback.msg}</p>}
      </div>
    );
  }

  return (
    <div className="detail-card">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button className="btn-edit-section" onClick={() => setEditing(true)}>Edit</button>
      </div>
      <div className="detail-row">
        <span className="detail-label">{t.name}</span>
        <span className="detail-value">{d.name || d.first_name || '—'}</span>
      </div>
      {d.last_name && (
        <div className="detail-row">
          <span className="detail-label">Last name</span>
          <span className="detail-value">{d.last_name}</span>
        </div>
      )}
      {(nicknames.length > 0 || d.preferred_name) && (
        <div className="detail-row">
          <span className="detail-label">{t.nickname}</span>
          <span className="detail-value">{d.preferred_name || nicknames.join(', ')}</span>
        </div>
      )}
      {/* #734 — birthday display. Full date when available; falls back
          to age + year tuple for legacy users who only have birth_year. */}
      {birthdayIso ? (
        <div className="detail-row">
          <span className="detail-label">{t.birthday || 'Birthday'}</span>
          <span className="detail-value">
            {new Date(birthdayIso + 'T00:00:00').toLocaleDateString(undefined, {
              year: 'numeric', month: 'long', day: 'numeric',
            })}
            {age ? ` (${age})` : ''}
          </span>
        </div>
      ) : age ? (
        <div className="detail-row">
          <span className="detail-label">{t.age}</span>
          <span className="detail-value">{age} ({d.birth_year})</span>
        </div>
      ) : null}
      <div className="detail-row">
        <span className="detail-label">{t.language}</span>
        <span className="detail-value">{data.language || '—'}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">{t.location}</span>
        <span className="detail-value">{d.location || '—'}</span>
      </div>
      {feedback && <p className={`save-feedback ${feedback.type}`}>{feedback.msg}</p>}
    </div>
  );
}

// Structured interests (#168) — eight categorised fields, one chip
// list per field. Each entry has a `source` ('intake' or 'conversation')
// — conversation-sourced entries get a small "auto" badge so families
// can see what was added by Berta vs what they entered themselves.
//
// Order and labels are kept in lockstep with FIELDS / FIELD_LABELS in
// app/services/structured_interests.py.
const STRUCTURED_INTEREST_FIELDS = [
  { key: 'music_artists',    label: 'Favourite singers / bands',
    placeholder: 'e.g. Queen, Frank Sinatra' },
  { key: 'music_genres',     label: 'Music styles / genres',
    placeholder: 'e.g. classical, 1950s jazz' },
  { key: 'sports_interests', label: 'Sports / teams',
    placeholder: 'e.g. Liverpool FC, Wimbledon tennis' },
  { key: 'screen_interests', label: 'Films / TV / actors',
    placeholder: 'e.g. Charlie Chaplin, Casablanca' },
  { key: 'places_interests', label: 'Favourite places',
    placeholder: 'e.g. Lake District, Barcelona' },
  { key: 'hobbies',          label: 'Hobbies',
    placeholder: 'e.g. gardening, crosswords' },
  // #711 — `career` interest bucket removed (8 → 7). Captured in the
  // dedicated Working life Portal tile; old entries rolled forward
  // into memories.identity.career_pride by migration 0067.
  { key: 'other_interests',  label: 'Other interests',
    placeholder: 'anything else worth knowing' },
];

// #171-E — map structured-interest field → (proposal_type, prefix)
// for looking up the learned weight from /api/intake/topic-weights.
// Mirrors _INTEREST_FIELD_TO_PROPOSAL in services/topic_weights.py;
// keep the two in sync.
const INTEREST_FIELD_TO_PROPOSAL = {
  music_artists:    { proposalType: 'media_play', prefix: 'music:' },
  music_genres:     { proposalType: 'media_play', prefix: 'music:' },
  screen_interests: { proposalType: 'media_play', prefix: 'video:' },
  sports_interests: { proposalType: 'topic',      prefix: '' },
  places_interests: { proposalType: 'topic',      prefix: '' },
  hobbies:          { proposalType: 'topic',      prefix: '' },
  // #711 — career mapping removed alongside the interest bucket.
  other_interests:  { proposalType: 'topic',      prefix: '' },
};

// Default weight for a topic with no signal yet — must match
// DEFAULT_WEIGHT in services/topic_weights.py.
const DEFAULT_TOPIC_WEIGHT = 50;

// Render the weight as a colour token. Matches behaviour ranges in
// services/topic_weights.behaviour_for_weight():
//  ≥70 propose freely (green), 30-69 occasional (amber/teal),
//  ≤29 don't propose (red), 0 corrected (dark red).
function weightColour(weight) {
  if (weight >= 70) return '#1a8a4a';
  if (weight >= 30) return '#078b88';
  if (weight >= 1)  return '#c0392b';
  return '#7a1810';
}

// #171-E — small weight gauge displayed inline next to each chip.
// Click opens an inline editor with a slider; saves via PUT.
function WeightGauge({ field, entry, weight, onSetWeight }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(weight);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(weight); }, [weight]);

  const colour = weightColour(weight);

  const save = async () => {
    setSaving(true);
    try {
      await onSetWeight(field, entry, draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        className="weight-gauge"
        onClick={() => setEditing(true)}
        title={`Weight ${weight} — click to adjust. Higher means Berta will propose this more often.`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          background: 'transparent', border: 'none', padding: '0 4px',
          cursor: 'pointer', fontSize: '0.72rem', color: '#888',
        }}
      >
        <span style={{
          display: 'inline-block', width: 28, height: 4,
          background: '#e8e4dc', borderRadius: 2, position: 'relative',
          overflow: 'hidden',
        }}>
          <span style={{
            display: 'block', height: '100%',
            width: `${Math.max(2, weight)}%`,
            background: colour,
          }} />
        </span>
        <span style={{ color: colour, fontWeight: 600 }}>{weight}</span>
      </button>
    );
  }

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: '#FFF8F4', border: '1px solid #E8DDD0',
      padding: '2px 8px', borderRadius: 999,
    }}>
      <input
        type="range" min="0" max="100" step="1"
        value={draft}
        onChange={e => setDraft(parseInt(e.target.value, 10))}
        style={{ width: 80 }}
        disabled={saving}
      />
      <span style={{ width: 24, textAlign: 'right', fontSize: '0.78rem', fontWeight: 600, color: weightColour(draft) }}>{draft}</span>
      <button onClick={save} disabled={saving}
        style={{ background: '#078b88', color: '#fff', border: 'none', borderRadius: 6, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 600, cursor: saving ? 'wait' : 'pointer' }}>
        {saving ? '…' : 'Save'}
      </button>
      <button onClick={() => { setDraft(weight); setEditing(false); }} disabled={saving}
        style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '0.72rem' }}>
        Cancel
      </button>
    </span>
  );
}

function InterestField({ field, label, placeholder, entries, onAdd, onRemove,
                        weights, onSetWeight }) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!draft.trim() || busy) return;
    // #311 — split on commas / semicolons / newlines so "knitting,
    // gardening" yields two chips. Helper trims + dedups intra-input
    // and against existing entries.
    const existingValues = (entries || []).map(e => e.entry);
    const newValues = splitChipInput(draft, existingValues);
    if (newValues.length === 0) {
      setDraft('');
      return;
    }
    setBusy(true);
    try {
      // onAdd takes one entry at a time. Call sequentially so the
      // backend persists each in turn (and any error stops the chain).
      for (const v of newValues) {
        await onAdd(v);
      }
      setDraft('');
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } };

  // #171-E — derive the (proposal_type, specification) for this field's
  // entries so we can look up each entry's learned weight.
  const mapping = INTEREST_FIELD_TO_PROPOSAL[field];

  const lookupWeight = (entry) => {
    if (!mapping || !weights) return DEFAULT_TOPIC_WEIGHT;
    const spec = `${mapping.prefix}${entry}`;
    const key = `${mapping.proposalType}::${spec}`;
    return weights[key] !== undefined ? weights[key] : DEFAULT_TOPIC_WEIGHT;
  };

  return (
    <div className="interest-field">
      <h4 className="interest-field-label">{label}</h4>
      {entries.length > 0 ? (
        <div className="chip-list">
          {entries.map((item, i) => {
            const w = lookupWeight(item.entry);
            return (
              <span key={`${field}-${i}`} className="chip">
                {item.entry}
                {item.source === 'conversation' && (
                  <span className="chip-source-badge" title="Auto-added from a conversation">auto</span>
                )}
                {/* #171-E — learned weight gauge */}
                {mapping && (
                  <WeightGauge
                    field={field}
                    entry={item.entry}
                    weight={w}
                    onSetWeight={onSetWeight}
                  />
                )}
                <button
                  className="chip-delete"
                  onClick={() => onRemove(item.entry)}
                  title="Remove"
                  disabled={busy}
                >&#10005;</button>
              </span>
            );
          })}
        </div>
      ) : (
        <p className="empty-state interest-field-empty">—</p>
      )}
      <div className="add-interest-row">
        <input
          className="add-interest-input"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder={placeholder}
          disabled={busy}
        />
        <button
          className="add-interest-btn"
          onClick={submit}
          disabled={busy || !draft.trim()}
        >Add</button>
      </div>
    </div>
  );
}

function InterestsSection({ data, t, onDataChange }) {
  // Server returns structured_interests as { field: [{entry, source, created_at}, ...] }.
  // Default each field to an empty list so the UI renders all 8 even on a fresh user.
  const initial = useMemo(() => {
    const si = data.structured_interests || {};
    const out = {};
    for (const f of STRUCTURED_INTEREST_FIELDS) {
      out[f.key] = Array.isArray(si[f.key]) ? si[f.key] : [];
    }
    return out;
  }, [data.structured_interests]);

  const [byField, setByField] = useState(initial);
  const [feedback, setFeedback] = useState(null);
  // #171-E — learned topic weights, keyed `${proposal_type}::${specification}`
  // so chip lookup is O(1). Falls back to DEFAULT_TOPIC_WEIGHT when a row
  // doesn't exist yet (cold start, no signals collected).
  const [weights, setWeights] = useState({});

  // If the parent reloads intake data (e.g. after a refresh), pick up
  // the new structured interests. Cheap: shallow-equal would be more
  // careful, but this section's data only changes on explicit reloads.
  useEffect(() => { setByField(initial); }, [initial]);

  // #171-E — fetch learned weights once on mount.
  useEffect(() => {
    const token = localStorage.getItem('am_token');
    fetch('/api/intake/topic-weights', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { weights: [] })
      .then(j => {
        const map = {};
        for (const w of (j.weights || [])) {
          const k = `${w.proposal_type}::${w.specification}`;
          map[k] = w.weight;
        }
        setWeights(map);
      })
      .catch(() => {});
  }, []);

  const flash = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 2200);
  };

  const handleAdd = async (field, entry) => {
    const token = localStorage.getItem('am_token');
    try {
      const res = await fetch(`/api/intake/structured-interests/${field}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ entry }),
      });
      if (!res.ok) throw new Error('save failed');
      // Optimistic insert if the server actually inserted (not a dup).
      const j = await res.json().catch(() => ({}));
      if (j.inserted) {
        setByField(prev => ({
          ...prev,
          [field]: [...(prev[field] || []), { entry: j.entry || entry, source: 'intake' }],
        }));
        if (onDataChange) {
          onDataChange({
            ...data,
            structured_interests: {
              ...(data.structured_interests || {}),
              [field]: [...((data.structured_interests || {})[field] || []), { entry: j.entry || entry, source: 'intake' }],
            },
          });
        }
      }
      flash('success', 'Saved');
    } catch {
      flash('error', 'Could not save');
    }
  };

  const handleRemove = async (field, entry) => {
    const token = localStorage.getItem('am_token');
    try {
      const res = await fetch(`/api/intake/structured-interests/${field}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ entry }),
      });
      if (!res.ok) throw new Error('remove failed');
      setByField(prev => ({
        ...prev,
        [field]: (prev[field] || []).filter(it => it.entry.toLowerCase() !== entry.toLowerCase()),
      }));
      if (onDataChange) {
        onDataChange({
          ...data,
          structured_interests: {
            ...(data.structured_interests || {}),
            [field]: ((data.structured_interests || {})[field] || []).filter(it => it.entry.toLowerCase() !== entry.toLowerCase()),
          },
        });
      }
      flash('success', 'Removed');
    } catch {
      flash('error', 'Could not remove');
    }
  };

  // #171-E — manual weight override. PUTs the new weight; updates
  // local state on success. encodeURIComponent on specification so
  // entries with spaces / colons (e.g. "music:Frank Sinatra") survive
  // the round trip.
  const handleSetWeight = async (field, entry, newWeight) => {
    const mapping = INTEREST_FIELD_TO_PROPOSAL[field];
    if (!mapping) return;
    const spec = `${mapping.prefix}${entry}`;
    const token = localStorage.getItem('am_token');
    try {
      const res = await fetch(
        `/api/intake/topic-weights/${encodeURIComponent(mapping.proposalType)}/${encodeURIComponent(spec)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ weight: newWeight }),
        }
      );
      if (!res.ok) throw new Error('save failed');
      const j = await res.json();
      setWeights(prev => ({ ...prev, [`${mapping.proposalType}::${spec}`]: j.weight }));
      flash('success', 'Weight updated');
    } catch {
      flash('error', 'Could not update weight');
    }
  };

  return (
    <div className="detail-card structured-interests">
      {STRUCTURED_INTEREST_FIELDS.map(f => (
        <InterestField
          key={f.key}
          field={f.key}
          label={f.label}
          placeholder={f.placeholder}
          entries={byField[f.key] || []}
          onAdd={(v) => handleAdd(f.key, v)}
          onRemove={(v) => handleRemove(f.key, v)}
          weights={weights}
          onSetWeight={handleSetWeight}
        />
      ))}
      {feedback && <p className={`save-feedback ${feedback.type}`}>{feedback.msg}</p>}
    </div>
  );
}

// #1107 — relation options now come from the shared single source of
// truth (web/src/i18n/relations.js), translated per language via
// t.relations. Removed the Portal copy (which had drifted: it carried a
// redundant 'other' alongside the 'family member' catch-all) so all four
// consumers share one list and gain Boyfriend/Girlfriend/Partner together.

function FamilySection({ data, t, onDataChange }) {
  const familyMembers = data.family_members || [];
  const elderlyName = (data.data && (data.data.name || '')) || '';
  const [members, setMembers] = useState(familyMembers);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newRelation, setNewRelation] = useState('family member');
  const [newRelationTo, setNewRelationTo] = useState(elderlyName);
  const [newNotes, setNewNotes] = useState('');
  // #735 — alive / deceased status. Defaults to 'alive' for new entries;
  // existing rows without status are treated as alive on both render
  // and save.
  const [newStatus, setNewStatus] = useState('alive');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', relation: '', relation_to: '', notes: '', status: 'alive' });
  const [deletingId, setDeletingId] = useState(null);

  const token = localStorage.getItem('am_token');

  const showFeedback = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 2500);
  };

  const addMember = async () => {
    const name = newName.trim();
    if (!name) return;
    setSaving(true);
    const memberData = {
      name,
      relation: newRelation,
      relation_to: newRelationTo.trim() || elderlyName,
      notes: newNotes.trim(),
      status: (newStatus === 'deceased' ? 'deceased' : 'alive'),
    };
    try {
      const res = await fetch('/api/memories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category: 'family', key: 'family_member', value: JSON.stringify(memberData) }),
      });
      if (!res.ok) throw new Error('Failed');
      const created = await res.json();
      const updated = [...members, { id: created.id, ...memberData }];
      setMembers(updated);
      if (onDataChange) onDataChange({ ...data, family_members: updated });
      setNewName(''); setNewRelation('other'); setNewRelationTo(elderlyName); setNewNotes(''); setNewStatus('alive');
      setShowAdd(false);
      showFeedback('success', 'Added');
    } catch {
      showFeedback('error', 'Could not add');
    }
    setSaving(false);
  };

  const deleteMember = async (id) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/memories/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed');
      const updated = members.filter(m => m.id !== id);
      setMembers(updated);
      if (onDataChange) onDataChange({ ...data, family_members: updated });
      setDeletingId(null);
      showFeedback('success', 'Removed');
    } catch {
      showFeedback('error', 'Could not delete');
    }
    setSaving(false);
  };

  const saveEdit = async (id) => {
    const name = editForm.name.trim();
    if (!name) return;
    setSaving(true);
    const memberData = {
      name,
      relation: editForm.relation,
      relation_to: editForm.relation_to.trim() || elderlyName,
      notes: editForm.notes.trim(),
      status: (editForm.status === 'deceased' ? 'deceased' : 'alive'),
    };
    try {
      const res = await fetch(`/api/memories/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ key: 'family_member', value: JSON.stringify(memberData) }),
      });
      if (!res.ok) throw new Error('Failed');
      const updated = members.map(m => m.id === id ? { ...m, ...memberData } : m);
      setMembers(updated);
      if (onDataChange) onDataChange({ ...data, family_members: updated });
      setEditingId(null);
      showFeedback('success', 'Saved');
    } catch {
      showFeedback('error', 'Could not save');
    }
    setSaving(false);
  };

  const thStyle = {
    textAlign: 'left', padding: '8px 12px', fontSize: '0.78rem', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: '#999',
    borderBottom: '2px solid #F0EDEA',
  };
  const tdStyle = { padding: '10px 12px', fontSize: '0.9rem', color: '#2C2C2C', borderBottom: '1px solid #F0EDEA', verticalAlign: 'middle' };
  const relLabel = (v) => relationLabel(t, v);

  return (
    <div className="detail-card" style={{ overflowX: 'auto' }}>
      {members.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Relation</th>
              <th style={thStyle}>To</th>
              <th style={thStyle}>Notes</th>
              {/* #735 — status column (alive / deceased). Sits to the
                  right of notes because for most users most of the time
                  the value is 'alive' and we want to keep the visual
                  weight off it. */}
              <th style={thStyle}>Status</th>
              <th style={{ ...thStyle, width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => {
              if (editingId === m.id) {
                return (
                  <tr key={m.id}>
                    <td style={tdStyle}>
                      <input className="edit-input" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} style={{ width: '100%', maxWidth: 'none' }} autoFocus />
                    </td>
                    <td style={tdStyle}>
                      <select className="edit-select" value={editForm.relation} onChange={e => setEditForm({ ...editForm, relation: e.target.value })}>
                        {relationOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <input className="edit-input" value={editForm.relation_to} onChange={e => setEditForm({ ...editForm, relation_to: e.target.value })} style={{ width: '100%', maxWidth: 'none' }} />
                    </td>
                    <td style={tdStyle}>
                      <input className="edit-input" value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} style={{ width: '100%', maxWidth: 'none' }} />
                    </td>
                    <td style={tdStyle}>
                      <select className="edit-select" value={editForm.status || 'alive'} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
                        <option value="alive">Alive</option>
                        <option value="deceased">Passed away</option>
                      </select>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn-save-section" onClick={() => saveEdit(m.id)} disabled={saving} style={{ padding: '4px 10px', fontSize: '0.78rem' }}>Save</button>
                        <button className="btn-cancel-section" onClick={() => setEditingId(null)} style={{ padding: '4px 10px', fontSize: '0.78rem' }}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                );
              }
              if (deletingId === m.id) {
                return (
                  <tr key={m.id}>
                    <td style={tdStyle}>{m.name}</td>
                    <td style={tdStyle} colSpan={4}>
                      <div className="confirm-delete">
                        <span>Delete this member?</span>
                        <button className="confirm-yes" onClick={() => deleteMember(m.id)} disabled={saving}>Yes</button>
                        <button className="confirm-no" onClick={() => setDeletingId(null)}>No</button>
                      </div>
                    </td>
                    <td style={tdStyle}></td>
                  </tr>
                );
              }
              const isDeceased = (m.status || 'alive') === 'deceased';
              return (
                <tr key={m.id || `fam-${m.name}`}>
                  <td style={{ ...tdStyle, fontWeight: 500 }}>{m.name}</td>
                  <td style={tdStyle}>{relLabel(m.relation)}</td>
                  <td style={tdStyle}>{m.relation_to}</td>
                  <td style={{ ...tdStyle, color: '#666', fontSize: '0.85rem' }}>{m.notes || '—'}</td>
                  <td style={tdStyle}>
                    {isDeceased ? (
                      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#666', background: '#F0EDEA', padding: '2px 8px', borderRadius: 10 }}>
                        Passed away
                      </span>
                    ) : (
                      <span style={{ fontSize: '0.85rem', color: '#bbb' }}>—</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {m.id && (
                      <div className="family-row-actions">
                        <button className="btn-icon" onClick={() => { setEditingId(m.id); setEditForm({ name: m.name, relation: m.relation, relation_to: m.relation_to, notes: m.notes || '', status: m.status || 'alive' }); }} title="Edit">&#9998;</button>
                        <button className="btn-icon danger" onClick={() => setDeletingId(m.id)} title="Delete">&#10005;</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className="empty-state">{t.noFamily}</p>
      )}

      {showAdd ? (
        <div style={{ marginTop: 16, padding: 16, background: '#FAFAF8', borderRadius: 10, border: '1px solid #E0D8D0' }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>Add family member</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input className="edit-input" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name *" style={{ flex: 1, minWidth: 140, maxWidth: 'none' }} />
              <select className="edit-select" value={newRelation} onChange={e => setNewRelation(e.target.value)}>
                {relationOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input className="edit-input" value={newRelationTo} onChange={e => setNewRelationTo(e.target.value)} placeholder={`Relation to (default: ${elderlyName})`} style={{ flex: 1, minWidth: 140, maxWidth: 'none' }} />
              <input className="edit-input" value={newNotes} onChange={e => setNewNotes(e.target.value)} placeholder="Notes (optional)" style={{ flex: 1, minWidth: 140, maxWidth: 'none' }} />
            </div>
            {/* #735 — alive / deceased status. Default 'alive' for new
                entries so existing flows don't change shape, but
                explicit selection is offered. */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ fontSize: '0.85rem', color: '#666', marginRight: 4 }}>Still with us?</label>
              <select className="edit-select" value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                <option value="alive">Yes</option>
                <option value="deceased">Passed away</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-save-section" onClick={addMember} disabled={saving || !newName.trim()}>Add</button>
              <button className="btn-cancel-section" onClick={() => { setShowAdd(false); setNewName(''); setNewNotes(''); setNewStatus('alive'); }}>Cancel</button>
            </div>
          </div>
        </div>
      ) : (
        <button
          className="add-interest-btn"
          onClick={() => { setShowAdd(true); setNewRelationTo(elderlyName); }}
          style={{ marginTop: 12, width: '100%', background: 'none', border: '1.5px dashed #D0C8C0', color: '#888' }}
        >
          + Add family member
        </button>
      )}
      {feedback && <p className={`save-feedback ${feedback.type}`}>{feedback.msg}</p>}
    </div>
  );
}

const FRIEND_FIELD_LABELS = {
  friend_name: 'Name',
  friend_location: 'Location',
  friend_how_met: 'How they met',
  friend_shared_interests: 'Shared interests',
  friend_status: 'Status',
};

const STATUS_OPTIONS = [
  { value: 'in_contact', label: 'In contact' },
  { value: 'lost_touch', label: 'Lost touch' },
  { value: 'deceased', label: 'Passed away' },
];

function parseFriendKey(key) {
  const m = key.match(/^friend_([^_]+)_(.+)$/);
  if (!m) return { prefix: 'unknown', field: key };
  return { prefix: m[1], field: m[2] };
}

function FriendsSection({ data, t }) {
  const friendMemories = data.friend_memories || [];
  const [friends, setFriends] = useState(() => {
    // Group memories by friend prefix
    const groups = {};
    for (const m of friendMemories) {
      if (m.key === 'friends_raw') continue;
      const { prefix, field } = parseFriendKey(m.key);
      if (!groups[prefix]) groups[prefix] = { _prefix: prefix };
      groups[prefix][field] = m.value;
    }
    return Object.values(groups);
  });
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newHowMet, setNewHowMet] = useState('');
  const [newInterests, setNewInterests] = useState('');
  const [newStatus, setNewStatus] = useState('in_contact');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const saveFriends = async (updatedFriends) => {
    setSaving(true);
    setFeedback(null);
    const token = localStorage.getItem('am_token');
    // Convert friends array to [{key, value}] memory format
    const memoryPairs = [];
    for (const fr of updatedFriends) {
      const prefix = fr._prefix || (fr.friend_name || 'unknown').toLowerCase().split(/\s+/)[0].replace(/[^a-z0-9]/g, '_');
      for (const field of ['friend_name', 'friend_location', 'friend_how_met', 'friend_shared_interests', 'friend_status']) {
        if (fr[field]) {
          memoryPairs.push({ key: `friend_${prefix}_${field}`, value: fr[field] });
        }
      }
    }
    try {
      const res = await fetch('/api/intake/review', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: data.user_id, data: { friend_memories: memoryPairs } }),
      });
      if (!res.ok) throw new Error('Save failed');
      setFeedback({ type: 'success', msg: 'Saved' });
    } catch {
      setFeedback({ type: 'error', msg: 'Could not save' });
    }
    setSaving(false);
    setTimeout(() => setFeedback(null), 2500);
  };

  const addFriend = () => {
    const name = newName.trim();
    if (!name) return;
    const prefix = name.toLowerCase().split(/\s+/)[0].replace(/[^a-z0-9]/g, '_');
    const newFriend = {
      _prefix: prefix,
      friend_name: name,
      friend_location: newLocation.trim() || null,
      friend_how_met: newHowMet.trim() || null,
      friend_shared_interests: newInterests.trim() || null,
      friend_status: newStatus,
    };
    const updated = [...friends, newFriend];
    setFriends(updated);
    setNewName(''); setNewLocation(''); setNewHowMet(''); setNewInterests(''); setNewStatus('in_contact');
    setShowAdd(false);
    saveFriends(updated);
  };

  const updateStatus = (idx, status) => {
    const updated = friends.map((f, i) => i === idx ? { ...f, friend_status: status } : f);
    setFriends(updated);
    saveFriends(updated);
  };

  const rawMemory = friendMemories.find(m => m.key === 'friends_raw');

  return (
    <>
      {rawMemory && (
        <div className="detail-card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#999', marginBottom: 8 }}>From intake</div>
          <p style={{ fontSize: '0.95rem', color: '#2C2C2C', lineHeight: 1.6 }}>{rawMemory.value}</p>
        </div>
      )}
      <div className="detail-card">
        {friends.length > 0 ? (
          <ul className="family-list">
            {friends.map((fr, i) => (
              <li key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>
                    {fr.friend_name}
                    {fr.friend_status === 'deceased' && (
                      <span style={{ marginLeft: 10, fontSize: '0.78rem', fontWeight: 500, color: '#999', fontStyle: 'italic' }}>passed away</span>
                    )}
                    {fr.friend_status === 'lost_touch' && (
                      <span style={{ marginLeft: 10, fontSize: '0.78rem', fontWeight: 500, color: '#B08030' }}>lost touch</span>
                    )}
                    {fr.friend_status === 'in_contact' && (
                      <span style={{ marginLeft: 10, fontSize: '0.78rem', fontWeight: 500, color: '#28A745' }}>in contact</span>
                    )}
                  </div>
                  {fr.friend_location && <div style={{ fontSize: '0.85rem', color: '#666' }}>Location: {fr.friend_location}</div>}
                  {fr.friend_how_met && <div style={{ fontSize: '0.85rem', color: '#666' }}>How met: {fr.friend_how_met}</div>}
                  {fr.friend_shared_interests && <div style={{ fontSize: '0.85rem', color: '#666' }}>Shared interests: {fr.friend_shared_interests}</div>}
                </div>
                <select
                  value={fr.friend_status || 'in_contact'}
                  onChange={e => updateStatus(i, e.target.value)}
                  disabled={saving}
                  style={{ padding: '4px 8px', borderRadius: 8, border: '1.5px solid #E0D8D0', fontSize: '0.8rem', fontFamily: 'inherit', background: '#FAFAF8', flexShrink: 0 }}
                >
                  {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </li>
            ))}
          </ul>
        ) : (
          <p className="empty-state">{t.noFriends || 'No friends added yet.'}</p>
        )}

        {showAdd ? (
          <div style={{ marginTop: 16, padding: 16, background: '#FAFAF8', borderRadius: 10, border: '1px solid #E0D8D0' }}>
            <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>Add a friend</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input className="add-interest-input" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name *" />
              <input className="add-interest-input" value={newLocation} onChange={e => setNewLocation(e.target.value)} placeholder="Location (optional)" />
              <input className="add-interest-input" value={newHowMet} onChange={e => setNewHowMet(e.target.value)} placeholder="How they met (optional)" />
              <input className="add-interest-input" value={newInterests} onChange={e => setNewInterests(e.target.value)} placeholder="Shared interests (optional)" />
              <select value={newStatus} onChange={e => setNewStatus(e.target.value)} style={{ padding: '8px 14px', borderRadius: 10, border: '1.5px solid #E0D8D0', fontSize: '0.9rem', fontFamily: 'inherit', background: '#FAFAF8' }}>
                {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="add-interest-btn" onClick={addFriend} disabled={saving || !newName.trim()}>Add</button>
                <button className="add-interest-btn" onClick={() => setShowAdd(false)} style={{ background: '#999' }}>Cancel</button>
              </div>
            </div>
          </div>
        ) : (
          <button
            className="add-interest-btn"
            onClick={() => setShowAdd(true)}
            style={{ marginTop: 12, width: '100%', background: 'none', border: '1.5px dashed #D0C8C0', color: '#888' }}
          >
            + Add a friend
          </button>
        )}
        {feedback && <p className={`save-feedback ${feedback.type}`}>{feedback.msg}</p>}
      </div>
    </>
  );
}

const HEALTH_CATEGORIES = [
  { value: 'cognitive', label: 'Cognitive' },
  { value: 'physical', label: 'Physical' },
  { value: 'medication', label: 'Medication' },
  { value: 'other', label: 'Other' },
];

function HealthSection({ data, t }) {
  const healthMemories = data.health_memories || [];
  const [notes, setNotes] = useState(healthMemories);
  const [newText, setNewText] = useState('');
  const [newCategory, setNewCategory] = useState('cognitive');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const token = localStorage.getItem('am_token');

  const showFeedback = (type, msg) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 2500);
  };

  const addNote = async () => {
    const val = newText.trim();
    if (!val) return;
    setSaving(true);
    try {
      const res = await fetch('/api/memories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ category: 'health', key: newCategory, value: val }),
      });
      if (!res.ok) throw new Error();
      const result = await res.json();
      setNotes(prev => [...prev, { id: result.id, key: newCategory, value: val }]);
      setNewText('');
      setNewCategory('cognitive');
      showFeedback('success', 'Added');
    } catch {
      showFeedback('error', 'Could not add');
    }
    setSaving(false);
  };

  const saveEdit = async (id) => {
    const val = editText.trim();
    if (!val) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/memories/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ value: val }),
      });
      if (!res.ok) throw new Error();
      setNotes(prev => prev.map(n => n.id === id ? { ...n, value: val } : n));
      setEditingId(null);
      showFeedback('success', 'Updated');
    } catch {
      showFeedback('error', 'Could not update');
    }
    setSaving(false);
  };

  const deleteNote = async (id) => {
    setSaving(true);
    try {
      const res = await fetch(`/api/memories/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      setNotes(prev => prev.filter(n => n.id !== id));
      setDeleteConfirm(null);
      showFeedback('success', 'Deleted');
    } catch {
      showFeedback('error', 'Could not delete');
    }
    setSaving(false);
  };

  const categoryLabel = (key) => {
    const cat = HEALTH_CATEGORIES.find(c => c.value === key);
    return cat ? cat.label : key;
  };

  return (
    <>
      <div style={{ background: '#FFF8F0', border: '1px solid #F0E0D0', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: '0.82rem', color: '#8A7050', lineHeight: 1.5 }}>
        These notes are visible to family only — not shown to your loved one.
      </div>

      <div className="detail-card">
        {notes.length > 0 ? (
          <ul className="family-list">
            {notes.map(note => (
              <li key={note.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                {editingId === note.id ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <textarea
                      className="add-interest-input"
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      rows={3}
                      style={{ resize: 'vertical' }}
                      disabled={saving}
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="add-interest-btn" onClick={() => saveEdit(note.id)} disabled={saving || !editText.trim()} style={{ fontSize: '0.8rem', padding: '6px 14px' }}>Save</button>
                      <button className="add-interest-btn" onClick={() => setEditingId(null)} style={{ fontSize: '0.8rem', padding: '6px 14px', background: '#999' }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#E8845A', marginRight: 8 }}>{categoryLabel(note.key)}</span>
                      <span style={{ fontSize: '0.95rem', color: '#2C2C2C' }}>{note.value}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button
                        onClick={() => { setEditingId(note.id); setEditText(note.value); }}
                        disabled={saving}
                        style={{ background: 'none', border: '1px solid #E0D8D0', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', fontFamily: 'inherit', color: '#666', cursor: 'pointer' }}
                      >Edit</button>
                      {deleteConfirm === note.id ? (
                        <>
                          <button
                            onClick={() => deleteNote(note.id)}
                            disabled={saving}
                            style={{ background: '#DC3545', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', fontFamily: 'inherit', color: '#fff', cursor: 'pointer' }}
                          >Confirm</button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            style={{ background: 'none', border: '1px solid #E0D8D0', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', fontFamily: 'inherit', color: '#666', cursor: 'pointer' }}
                          >Cancel</button>
                        </>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(note.id)}
                          disabled={saving}
                          style={{ background: 'none', border: '1px solid #E0D8D0', borderRadius: 6, padding: '4px 10px', fontSize: '0.78rem', fontFamily: 'inherit', color: '#DC3545', cursor: 'pointer' }}
                        >Delete</button>
                      )}
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="empty-state">{t.noHealth || 'No health notes yet.'}</p>
        )}
      </div>

      {/* Add health note form */}
      <div className="detail-card" style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>Add health note</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea
            className="add-interest-input"
            value={newText}
            onChange={e => setNewText(e.target.value)}
            placeholder="e.g. Has trouble remembering names, takes blood pressure medication daily..."
            rows={3}
            style={{ resize: 'vertical' }}
            disabled={saving}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={newCategory}
              onChange={e => setNewCategory(e.target.value)}
              disabled={saving}
              style={{ padding: '8px 14px', borderRadius: 10, border: '1.5px solid #E0D8D0', fontSize: '0.85rem', fontFamily: 'inherit', background: '#FAFAF8', color: '#2C2C2C' }}
            >
              {HEALTH_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <button className="add-interest-btn" onClick={addNote} disabled={saving || !newText.trim()}>Add</button>
          </div>
        </div>
        {feedback && <p className={`save-feedback ${feedback.type}`}>{feedback.msg}</p>}
      </div>

      <p className="sensitive-note">🔒 {t.familyOnly}</p>
    </>
  );
}

// #244-C — Trial banner. Displayed at the top of the Portal when the
// user is in trial state. Color-coded: green > 5 days, amber 3–5,
// red ≤ 2. Hidden once subscription_status flips to 'active' (paid).
// #300 — Portal home cockpit. At-a-glance stats above the section-cards
// grid. v1 ships the talk-time gauge. Self-fetches from
// /api/portal/cockpit; degrades gracefully if the endpoint fails or
// the elderly user isn't yet linked (returns nothing).
function PortalCockpit() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('am_token');
    if (!token) return;
    fetch('/api/portal/cockpit', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  if (!loaded) return null;  // silent until first load
  if (!data) return null;
  // Need at least the service-capacity panel to render anything.
  // Older API responses only have `talk_time`; newer have
  // `service_capacity` + `real_talk_time` (#346 follow-up — dual metric).
  const sc = data.service_capacity || data.talk_time;
  if (!sc) return null;

  const realMin = data.real_talk_time?.minutes_this_period;
  const pct = Math.min(100, Math.round((sc.percent_used || 0) * 100));
  const overQuota = sc.within_quota === false;

  // Color: green > 50% remaining, amber 20–50%, red ≤ 20% or over.
  const remainingPct = 100 - pct;
  let barColor = '#27AE60', textColor = '#1b5e20';
  if (overQuota || remainingPct <= 20) { barColor = '#C0392B'; textColor = '#b71c1c'; }
  else if (remainingPct <= 50) { barColor = '#E8845A'; textColor = '#e65100'; }

  const resetPretty = sc.next_reset_date
    ? new Date(sc.next_reset_date).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
    : null;

  // Format real-talk-time naturally: "2 minutes" or "1 hour 14 minutes"
  const fmtRealTime = (mins) => {
    if (mins === null || mins === undefined) return null;
    const m = Math.round(mins);
    if (m === 0) return 'Less than a minute';
    if (m < 60) return `${m} ${m === 1 ? 'minute' : 'minutes'}`;
    const h = Math.floor(m / 60);
    const rem = m % 60;
    if (rem === 0) return `${h} ${h === 1 ? 'hour' : 'hours'}`;
    return `${h} ${h === 1 ? 'hour' : 'hours'} ${rem} ${rem === 1 ? 'minute' : 'minutes'}`;
  };

  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '20px 24px',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      marginTop: 16, marginBottom: 20,
    }}>
      <div style={{ fontSize: '0.85rem', color: '#888', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        This month so far
      </div>

      {/* Real talk time — honest sum of conversation durations. */}
      {realMin !== null && realMin !== undefined && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 2 }}>
            Real chat time
          </div>
          <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#2C2C2C' }}>
            {fmtRealTime(realMin)}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#aaa', marginTop: 2, fontStyle: 'italic' }}>
            Sum of conversation durations across the month.
          </div>
        </div>
      )}

      {/* Service capacity — token-based, drives the cap. */}
      <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 4 }}>
        Service capacity used
      </div>
      <div style={{ fontSize: '1.1rem', fontWeight: 700, color: textColor, marginBottom: 8 }}>
        {overQuota
          ? 'Over monthly capacity — chat is paused until reset'
          : `${pct}% of monthly capacity used`}
      </div>
      <div style={{
        background: '#EDE9E4', borderRadius: 99, height: 10, overflow: 'hidden',
      }}>
        <div style={{
          width: `${pct}%`,
          height: '100%',
          background: barColor,
          borderRadius: 99,
          transition: 'width 0.5s ease',
        }} />
      </div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap',
        gap: 12, marginTop: 10, fontSize: '0.82rem', color: '#666',
      }}>
        <span>
          Capacity is a monthly compute budget — not the same as real chat time.
        </span>
        {resetPretty && <span>Resets {resetPretty}</span>}
      </div>
    </div>
  );
}


// #244-C — trial banner shown on every Portal page until payment is set up.
// `companionName` is the configured companion name for THIS family's elderly
// user (not a constant). Falls back to a generic "the companion" if it
// isn't loaded yet — better than hardcoding "Berta" which is just the dev
// default and changes per user.
function TrialBanner({ user, companionName }) {
  if (!user || user.subscription_status === 'active') return null;
  if (!user.trial_ends_at) return null;
  const ends = new Date(user.trial_ends_at);
  const now = new Date();
  const daysLeft = Math.ceil((ends - now) / (1000 * 60 * 60 * 24));

  let bg = '#e8f5e9', fg = '#1b5e20';
  if (daysLeft <= 2) { bg = '#ffebee'; fg = '#b71c1c'; }
  else if (daysLeft <= 5) { bg = '#fff3e0'; fg = '#e65100'; }

  const message = daysLeft > 0
    ? `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left in your trial.`
    : `Your trial ended ${Math.abs(daysLeft)} ${Math.abs(daysLeft) === 1 ? 'day' : 'days'} ago.`;

  const name = (companionName || '').trim() || 'The companion';

  return (
    <div style={{
      background: bg, color: fg,
      padding: '10px 16px', borderRadius: 8,
      margin: '12px 16px 0', fontSize: '0.9rem',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, flexWrap: 'wrap',
    }}>
      <span><strong>{message}</strong> {name} will keep working — payment isn't enabled yet.</span>
      {/* Set up payment CTA wired to the future billing flow. v1: link
          parked. */}
    </div>
  );
}


// #244-B/D — Account self-service: profile edit, password change, danger zone.
function AccountSection() {
  const { t } = useLang();  // #1107 — translated relation labels
  const [me, setMe] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [relation, setRelation] = useState('');
  const [language, setLanguage] = useState('en');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);

  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const token = localStorage.getItem('am_token');
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setMe(data);
          setName(data.name || '');
          setEmail(data.email || '');
          setRelation(data.relation_to_elderly || '');
          setLanguage(data.preferred_language || 'en');
        }
      });
  }, []); // eslint-disable-line

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMsg(null);
    try {
      const res = await fetch('/api/auth/me', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name, email, relation_to_elderly: relation || null,
          preferred_language: language,
        }),
      });
      if (res.ok) {
        setProfileMsg({ kind: 'ok', text: 'Profile saved.' });
      } else {
        const data = await res.json().catch(() => ({}));
        setProfileMsg({ kind: 'err', text: data.detail || 'Save failed.' });
      }
    } catch {
      setProfileMsg({ kind: 'err', text: 'Save failed.' });
    }
    setProfileSaving(false);
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwSaving(true);
    setPwMsg(null);
    try {
      const res = await fetch('/api/auth/me/password', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: curPw, new_password: newPw }),
      });
      if (res.ok) {
        setPwMsg({ kind: 'ok', text: 'Password changed.' });
        setCurPw(''); setNewPw('');
      } else {
        const data = await res.json().catch(() => ({}));
        setPwMsg({ kind: 'err', text: data.detail || 'Change failed.' });
      }
    } catch {
      setPwMsg({ kind: 'err', text: 'Change failed.' });
    }
    setPwSaving(false);
  };

  const deleteAccount = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch('/api/auth/me', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_email: confirmEmail }),
      });
      if (res.ok) {
        // Account is gone. Clear storage and bounce to landing.
        localStorage.removeItem('am_token');
        localStorage.removeItem('am_user');
        navigate('/');
      } else {
        const data = await res.json().catch(() => ({}));
        setDeleteError(data.detail || 'Delete failed.');
      }
    } catch {
      setDeleteError('Delete failed.');
    }
    setDeleting(false);
  };

  if (!me) return <div className="loading-spinner">Loading…</div>;

  return (
    <>
      {/* Trial status — shown here as well as the Portal banner (this
          gives the user one place to see + future "manage payment" link). */}
      <div className="detail-card" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 8px' }}>Subscription</h3>
        <p style={{ margin: 0, color: '#444', fontSize: '0.9rem' }}>
          Status: <strong>{me.subscription_status || 'trial'}</strong>
          {me.trial_ends_at && me.subscription_status === 'trial' && (
            <> · trial ends {new Date(me.trial_ends_at).toLocaleDateString()}</>
          )}
        </p>
        <p style={{ margin: '6px 0 0', color: '#888', fontSize: '0.8rem' }}>
          Payment isn't enabled yet — the companion will keep working past the trial date until billing ships.
        </p>
      </div>

      {/* Profile edit */}
      <div className="detail-card" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px' }}>Your details</h3>
        <form onSubmit={saveProfile} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
            Name
            <input className="input-field" value={name} onChange={e => setName(e.target.value)} required style={{ marginTop: 4 }} />
          </label>
          <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
            Email
            <input className="input-field" type="email" value={email} onChange={e => setEmail(e.target.value)} required style={{ marginTop: 4 }} />
            <span style={{ fontSize: '0.75rem', color: '#888', display: 'block', marginTop: 4 }}>
              Email change saves immediately. (Verification-by-link is a future enhancement.)
            </span>
          </label>
          <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
            {t.signup.relationLabel}
            <select className="input-field" value={relation} onChange={e => setRelation(e.target.value)} style={{ marginTop: 4 }}>
              <option value="">{t.signup.relationPlaceholder}</option>
              {relationOptions(t).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
            Preferred language
            <select className="input-field" value={language} onChange={e => setLanguage(e.target.value)} style={{ marginTop: 4 }}>
              <option value="en">English</option>
              <option value="nl">Nederlands</option>
              <option value="de">Deutsch</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="it">Italiano</option>
              <option value="pt">Português</option>
            </select>
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            <button className="btn-save" type="submit" disabled={profileSaving}>
              {profileSaving ? 'Saving…' : 'Save profile'}
            </button>
            {profileMsg && (
              <span style={{ fontSize: '0.85rem', color: profileMsg.kind === 'ok' ? '#4caf50' : '#c0392b' }}>
                {profileMsg.text}
              </span>
            )}
          </div>
        </form>
      </div>

      {/* Password change */}
      <div className="detail-card" style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px' }}>Change password</h3>
        <form onSubmit={savePassword} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
            Current password
            <input className="input-field" type="password" value={curPw} onChange={e => setCurPw(e.target.value)} required style={{ marginTop: 4 }} />
          </label>
          <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
            New password
            <input className="input-field" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required minLength={8} style={{ marginTop: 4 }} />
          </label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            <button className="btn-save" type="submit" disabled={pwSaving || !curPw || newPw.length < 8}>
              {pwSaving ? 'Saving…' : 'Change password'}
            </button>
            {pwMsg && (
              <span style={{ fontSize: '0.85rem', color: pwMsg.kind === 'ok' ? '#4caf50' : '#c0392b' }}>
                {pwMsg.text}
              </span>
            )}
          </div>
        </form>
      </div>

      {/* Danger zone */}
      <div className="detail-card" style={{ marginBottom: 16, border: '1.5px solid #fbe9e7' }}>
        <h3 style={{ margin: '0 0 8px', color: '#c0392b' }}>Danger zone</h3>
        <p style={{ margin: '0 0 12px', color: '#666', fontSize: '0.9rem' }}>
          Deleting your account removes all data — your profile, the elderly person's data, conversations, photos, reminders, and alerts. This cannot be undone.
        </p>
        {!deleteConfirmStep ? (
          <button
            onClick={() => setDeleteConfirmStep(true)}
            style={{ background: '#fff', color: '#c0392b', border: '1.5px solid #c0392b', borderRadius: 8, padding: '10px 16px', fontWeight: 600, cursor: 'pointer' }}
          >
            Delete my account
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ margin: 0, color: '#444', fontSize: '0.9rem' }}>
              Type your account email <strong>{me.email}</strong> to confirm:
            </p>
            <input
              className="input-field"
              type="email"
              value={confirmEmail}
              onChange={e => setConfirmEmail(e.target.value)}
              placeholder={me.email}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={deleteAccount}
                disabled={deleting || confirmEmail.toLowerCase().trim() !== (me.email || '').toLowerCase()}
                style={{ background: '#c0392b', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 600, cursor: 'pointer' }}
              >
                {deleting ? 'Deleting…' : 'Permanently delete account'}
              </button>
              <button
                onClick={() => { setDeleteConfirmStep(false); setConfirmEmail(''); setDeleteError(null); }}
                style={{ background: 'none', border: '1px solid #ccc', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
            {deleteError && (
              <span style={{ fontSize: '0.85rem', color: '#c0392b' }}>{deleteError}</span>
            )}
          </div>
        )}
      </div>
    </>
  );
}


// #287 — DisclaimerGate. Wraps a page section and blocks the children
// behind a one-time modal until the user has accepted a versioned
// disclaimer slug. Reusable for any high-stakes feature page; v1 only
// gates /portal/alerts.
//
// On first render fetches /api/auth/me to read disclaimers_accepted.
// If `slug` isn't in the map, renders the modal. Click "I understand"
// → POST /api/disclaimers/:slug/accept → re-fetch → show children.
function DisclaimerGate({ slug, title, body, children }) {
  const [accepted, setAccepted] = useState(null);  // null = loading
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem('am_token');

  useEffect(() => {
    if (!token) { setAccepted(false); return; }
    fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const map = (data && data.disclaimers_accepted) || {};
        setAccepted(Boolean(map[slug]));
      })
      .catch(() => setAccepted(false));
  }, [slug]);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`/api/disclaimers/${slug}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setAccepted(true);
    } catch { /* ignore */ }
    setSubmitting(false);
  };

  if (accepted === null) {
    return <div className="loading-spinner">Loading…</div>;
  }
  if (accepted) {
    return children;
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      zIndex: 100,
    }}>
      <div style={{
        background: '#fff',
        maxWidth: 480,
        width: '100%',
        borderRadius: 16,
        padding: 28,
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
      }}>
        <h2 style={{ margin: '0 0 12px', fontSize: '1.25rem', color: '#2C2C2C' }}>{title}</h2>
        <p style={{ margin: '0 0 20px', color: '#444', lineHeight: 1.55, fontSize: '0.95rem' }}>{body}</p>
        <button
          onClick={handleAccept}
          disabled={submitting}
          style={{
            background: '#E8845A',
            color: '#fff',
            border: 'none',
            borderRadius: 50,
            padding: '12px 24px',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: 'pointer',
            width: '100%',
          }}
        >
          {submitting ? '…' : 'I understand'}
        </button>
      </div>
    </div>
  );
}


function AlertsSection() {
  const [alerts, setAlerts] = useState([]);
  const [reminderAlerts, setReminderAlerts] = useState([]);
  // #270 — operational/system alerts (quota_warning_80 / quota_exceeded
  // for now; future kinds reuse the same table). Distinct from
  // safeguarding/reminder alerts — these are about service state, not
  // about the elderly user's wellbeing.
  const [systemAlerts, setSystemAlerts] = useState([]);
  const [notifSettings, setNotifSettings] = useState({ notification_email: '', notify_on_missed_reminder: false });
  const [loading, setLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifFeedback, setNotifFeedback] = useState(null);

  const token = localStorage.getItem('am_token');

  useEffect(() => {
    if (!token) return;
    Promise.all([
      fetch('/api/alerts', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
      fetch('/api/reminder-alerts', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
      fetch('/api/system-alerts', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
      fetch('/api/notification-settings', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
    ]).then(([a, ra, sa, ns]) => {
      setAlerts(a || []);
      setReminderAlerts(ra || []);
      setSystemAlerts(sa || []);
      if (ns) setNotifSettings({ notification_email: ns.notification_email || '', notify_on_missed_reminder: ns.notify_on_missed_reminder || false });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []); // eslint-disable-line

  // #270 — mark a system alert as seen.
  const markSystemSeen = async (id) => {
    try {
      const res = await fetch(`/api/system-alerts/${id}/seen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSystemAlerts(prev => prev.map(a => a.id === id ? { ...a, status: 'seen', seen_at: new Date().toISOString() } : a));
      }
    } catch { /* ignore */ }
  };

  // #270, #303 — render copy per kind. Keeps strings inline for v1;
  // localise when other languages need the Portal beyond English.
  const systemAlertCopy = (a) => {
    const p = a.payload || {};
    if (a.kind === 'quota_warning_80') {
      return {
        title: 'Chat allowance nearly full',
        body: `Your loved one has used 80% of this month's chat time. Lena will pause when it reaches 100% — usually a few days off.`,
      };
    }
    if (a.kind === 'quota_exceeded') {
      return {
        title: 'This month’s chat allowance used',
        body: 'Lena will be quiet until the allowance resets at the start of next month. Important reminders will still come through.',
      };
    }
    if (a.kind === 'quota_declined_call') {
      // #303 — per-period roll-up: how many times has the over-quota
      // user tried to reach the companion this month?
      const n = p.attempts_count || 1;
      const reset = p.reset_date ? new Date(p.reset_date).toLocaleDateString() : 'next month';
      const last = p.last_attempted_at ? new Date(p.last_attempted_at).toLocaleString() : null;
      return {
        title: 'Your loved one is trying to reach the companion',
        body: (
          `Your loved one has tried to start a call ${n} ${n === 1 ? 'time' : 'times'} ` +
          `this month after the chat time was used up. ` +
          `The companion will be available again on ${reset}. ` +
          (last ? `Last attempt: ${last}.` : '')
        ),
      };
    }
    return { title: a.kind, body: '' };
  };

  const markSeen = async (id) => {
    try {
      const res = await fetch(`/api/alerts/${id}/seen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setAlerts(prev => prev.map(a => a.id === id ? { ...a, status: 'seen', seen_at: new Date().toISOString() } : a));
      }
    } catch { /* ignore */ }
  };

  const acknowledgeReminderAlert = async (id) => {
    try {
      const res = await fetch(`/api/reminder-alerts/${id}/acknowledge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setReminderAlerts(prev => prev.map(a => a.id === id ? { ...a, acknowledged_at: new Date().toISOString() } : a));
      }
    } catch { /* ignore */ }
  };

  const saveNotifSettings = async () => {
    setNotifSaving(true);
    setNotifFeedback(null);
    try {
      const res = await fetch('/api/notification-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(notifSettings),
      });
      if (!res.ok) throw new Error();
      setNotifFeedback({ type: 'success', msg: 'Saved' });
    } catch {
      setNotifFeedback({ type: 'error', msg: 'Could not save' });
    }
    setNotifSaving(false);
    setTimeout(() => setNotifFeedback(null), 2500);
  };

  if (loading) return <div className="loading-spinner">Loading...</div>;

  const unacknowledgedReminder = reminderAlerts.filter(a => !a.acknowledged_at);
  const unseenSafeguarding = alerts.filter(a => a.status !== 'seen');
  const unseenSystem = systemAlerts.filter(a => a.status !== 'seen');
  const noAlertsAtAll = alerts.length === 0 && reminderAlerts.length === 0 && systemAlerts.length === 0;

  return (
    <>
      {/* ── System alerts (#270 — quota etc.) ── */}
      {systemAlerts.length > 0 && (
        <div className="detail-card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>
            Service alerts {unseenSystem.length > 0 && (
              <span style={{ background: '#3a8fb7', color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: '0.75rem', marginLeft: 8 }}>
                {unseenSystem.length}
              </span>
            )}
          </div>
          {systemAlerts.map(a => {
            const copy = systemAlertCopy(a);
            return (
              <div key={a.id} className={`alert-card ${a.status === 'seen' ? 'seen' : ''}`}>
                <div className="alert-date">
                  {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                </div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{copy.title}</div>
                <p className="alert-message">{copy.body}</p>
                {a.status !== 'seen' && (
                  <button className="btn-mark-seen" onClick={() => markSystemSeen(a.id)}>
                    Mark as seen
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Missed-call alerts (#274 — informational; emails removed) ── */}
      {reminderAlerts.length > 0 && (
        <div className="detail-card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>
            Missed calls {unacknowledgedReminder.length > 0 && (
              <span style={{ background: '#3a8fb7', color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: '0.75rem', marginLeft: 8 }}>
                {unacknowledgedReminder.length}
              </span>
            )}
          </div>
          {reminderAlerts.map(a => (
            <div key={a.id} className={`alert-card ${a.acknowledged_at ? 'seen' : ''}`}>
              <div className="alert-date">
                {a.last_attempted_at ? new Date(a.last_attempted_at).toLocaleString() : ''}
              </div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                {a.task}
              </div>
              <p className="alert-message">
                We tried to reach your loved one for this reminder {a.attempt_count} {a.attempt_count === 1 ? 'time' : 'times'} but didn't get an answer.
                You may want to check in with them when convenient.
              </p>
              {!a.acknowledged_at ? (
                <button className="btn-mark-seen" onClick={() => acknowledgeReminderAlert(a.id)}>
                  Mark as seen
                </button>
              ) : (
                <div style={{ fontSize: '0.75rem', color: '#bbb', marginTop: 6 }}>Marked as seen</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Safeguarding alerts ── */}
      {noAlertsAtAll ? (
        <div className="detail-card">
          <p className="empty-state">No alerts at this time. That's good news.</p>
        </div>
      ) : alerts.length > 0 && (
        <div className="detail-card" style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>
            Wellbeing alerts {unseenSafeguarding.length > 0 && (
              <span style={{ background: '#c0392b', color: '#fff', borderRadius: 10, padding: '2px 8px', fontSize: '0.75rem', marginLeft: 8 }}>
                {unseenSafeguarding.length}
              </span>
            )}
          </div>
          {alerts.map(alert => (
            <div key={alert.id} className={`alert-card ${alert.status === 'seen' ? 'seen' : ''}`}>
              <div className="alert-date">
                {alert.created_at ? new Date(alert.created_at).toLocaleString() : ''}
              </div>
              <p className="alert-message">
                We noticed something that may need your attention. Please check in with your loved one when you can.
              </p>
              <div className="alert-crisis-line">
                If you or your loved one need immediate support, please call a crisis line:
                <br /><strong>NL:</strong> 113 Zelfmoordpreventie — 113 or 0800-0113
                <br /><strong>UK:</strong> Samaritans — 116 123
                <br /><strong>ES:</strong> Telefono de la Esperanza — 717 003 717
              </div>
              {alert.status !== 'seen' && (
                <button className="btn-mark-seen" onClick={() => markSeen(alert.id)}>
                  Mark as seen
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Notification settings ── */}
      <div className="detail-card" style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>Email notifications</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="email"
            className="add-interest-input"
            placeholder="Your email address"
            value={notifSettings.notification_email}
            onChange={e => setNotifSettings(p => ({ ...p, notification_email: e.target.value }))}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.88rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={notifSettings.notify_on_missed_reminder}
              onChange={e => setNotifSettings(p => ({ ...p, notify_on_missed_reminder: e.target.checked }))}
            />
            Email me when a reminder is missed (3 unanswered calls)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="add-interest-btn" onClick={saveNotifSettings} disabled={notifSaving}>
              {notifSaving ? 'Saving...' : 'Save'}
            </button>
            {notifFeedback && (
              <span style={{ fontSize: '0.82rem', color: notifFeedback.type === 'success' ? '#27ae60' : '#c0392b' }}>
                {notifFeedback.msg}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function PhotosSection() {
  const [photos, setPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [description, setDescription] = useState('');
  const [photoDate, setPhotoDate] = useState('');

  const token = localStorage.getItem('am_token');

  const loadPhotos = async () => {
    try {
      const res = await fetch('/api/photos', { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setPhotos(await res.json());
    } catch { /* ignore */ }
    setLoadingPhotos(false);
  };

  useEffect(() => { loadPhotos(); }, []); // eslint-disable-line

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setFeedback(null);
    const formData = new FormData();
    formData.append('file', selectedFile);
    if (description.trim()) formData.append('description', description.trim());
    if (photoDate) formData.append('photo_date', photoDate);
    try {
      const res = await fetch('/api/photos/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      setFeedback({ type: 'success', msg: 'Photo uploaded' });
      setSelectedFile(null);
      setPreview(null);
      setDescription('');
      setPhotoDate('');
      loadPhotos();
    } catch {
      setFeedback({ type: 'error', msg: 'Upload failed' });
    }
    setUploading(false);
    setTimeout(() => setFeedback(null), 2500);
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`/api/photos/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setPhotos(prev => prev.filter(p => p.id !== id));
    } catch { /* ignore */ }
  };

  if (loadingPhotos) return <div className="loading-spinner">Loading...</div>;

  return (
    <>
      <div className="detail-card">
        <div className="photos-upload-area">
          <label className="photos-upload-btn" style={{ position: 'relative' }}>
            Choose photo
            <input type="file" accept="image/*" onChange={handleFileSelect} style={{ display: 'none' }} />
          </label>
          {selectedFile && (
            <div className="photos-fields">
              <input
                type="text"
                placeholder="Description (optional)"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
              <input
                type="date"
                value={photoDate}
                onChange={e => setPhotoDate(e.target.value)}
                title="When was this photo taken?"
              />
              <button className="photos-upload-btn" onClick={handleUpload} disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          )}
        </div>
        {preview && (
          <div className="photos-preview">
            <img src={preview} alt="Preview" />
          </div>
        )}
        {feedback && <p className={`save-feedback ${feedback.type}`}>{feedback.msg}</p>}
      </div>

      {photos.length > 0 ? (
        <div className="photos-grid">
          {photos.map(photo => (
            <div key={photo.id} className="photo-card">
              <img src={photo.url} alt={photo.description || photo.original_filename || 'Photo'} />
              <div className="photo-card-info">
                <button className="photo-card-delete" onClick={() => handleDelete(photo.id)} title="Delete">&#10005;</button>
                {photo.description && <div className="photo-card-desc">{photo.description}</div>}
                {photo.photo_date && <div className="photo-card-date">{photo.photo_date}</div>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="empty-state" style={{ marginTop: 16 }}>No photos uploaded yet.</p>
      )}
    </>
  );
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function RemindersSection({ t }) {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [task, setTask] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [reminderType, setReminderType] = useState('normal');
  const [scheduledTime, setScheduledTime] = useState('09:00');
  const [recurrence, setRecurrence] = useState('once');
  const [recurDays, setRecurDays] = useState([]);
  const [recurInterval, setRecurInterval] = useState(2);
  // Migration 0060 — per-reminder delivery preference for new-reminder
  // form. Default 'call' preserves the pre-toggle behavior.
  const [deliveryMode, setDeliveryMode] = useState('call');
  const [bdayPerson, setBdayPerson] = useState('');
  const [bdayCustomName, setBdayCustomName] = useState('');
  const [bdayDate, setBdayDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // #200 PR-B — companion-call history + voice_engagement banner
  const [companionCalls, setCompanionCalls] = useState([]);
  const [voiceEngagement, setVoiceEngagement] = useState('active');
  const [elderlyName, setElderlyName] = useState(null);
  // #204 — reminder safety disclaimer. undefined while /me is in flight,
  // null when never acknowledged, ISO timestamp once accepted.
  const [disclaimerAckAt, setDisclaimerAckAt] = useState(undefined);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [disclaimerModalOpen, setDisclaimerModalOpen] = useState(false);

  const token = localStorage.getItem('am_token');

  const fetchReminders = async () => {
    try {
      const res = await fetch('/api/reminders', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setReminders(data);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchCompanionCalls = async () => {
    try {
      const res = await fetch('/api/intake/companion-calls', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCompanionCalls(data.calls || []);
        setVoiceEngagement(data.voice_engagement || 'active');
      }
    } catch { /* ignore */ }
  };

  // Pull elderly name from intake review for the banner — only used to
  // personalise the message ("Eduard hasn't been picking up").
  const fetchElderlyName = async () => {
    try {
      const res = await fetch('/api/intake/review', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const d = (data && data.data) || {};
        setElderlyName(d.first_name || d.name || null);
      }
    } catch { /* ignore */ }
  };

  const fetchDisclaimerStatus = async () => {
    try {
      const res = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDisclaimerAckAt(data.reminder_disclaimer_acknowledged_at ?? null);
      } else {
        setDisclaimerAckAt(null);
      }
    } catch { setDisclaimerAckAt(null); }
  };

  const acknowledgeDisclaimer = async () => {
    if (!disclaimerChecked || acknowledging) return;
    setAcknowledging(true);
    try {
      const res = await fetch('/api/auth/acknowledge-reminder-disclaimer', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDisclaimerAckAt(data.reminder_disclaimer_acknowledged_at);
      }
    } catch { /* ignore — UI stays in pending state, retry possible */ }
    setAcknowledging(false);
  };

  useEffect(() => {
    fetchReminders();
    fetchCompanionCalls();
    fetchElderlyName();
    fetchDisclaimerStatus();
  }, []); // eslint-disable-line

  const toggleRecurDay = (d) => {
    setRecurDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  const addReminder = async () => {
    const val = task.trim();
    if (!val) return;
    setSaving(true);
    try {
      const body = {
        task: val,
        source: 'family',
        reminder_type: reminderType,
        delivery_mode: deliveryMode,
      };
      if (reminderType === 'normal') {
        if (dueDate) body.due_date = dueDate;
      } else {
        body.scheduled_time = scheduledTime;
        body.recurrence = recurrence;
        if (recurrence === 'once' && dueDate) body.due_date = dueDate;
        if (recurrence === 'weekly') body.recur_days = recurDays;
        if (recurrence === 'interval') body.recur_interval_days = recurInterval;
      }
      const res = await fetch('/api/reminders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      setTask('');
      setDueDate('');
      setReminderType('normal');
      setScheduledTime('09:00');
      setRecurrence('once');
      setRecurDays([]);
      setRecurInterval(2);
      setFeedback({ type: 'success', msg: 'Added' });
      await fetchReminders();
    } catch {
      setFeedback({ type: 'error', msg: 'Could not add' });
    }
    setSaving(false);
    setTimeout(() => setFeedback(null), 2500);
  };

  const knownPeople = [];

  const addBirthday = async () => {
    const name = bdayPerson === '__other__' ? bdayCustomName.trim() : bdayPerson;
    const date = bdayDate.trim();
    if (!name || !date) return;
    setSaving(true);
    try {
      const res = await fetch('/api/reminders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          task: `${name}'s birthday`,
          source: 'family',
          recurring: true,
          recur_date: date,
        }),
      });
      if (!res.ok) throw new Error();
      setBdayPerson('');
      setBdayCustomName('');
      setBdayDate('');
      setFeedback({ type: 'success', msg: 'Added' });
      await fetchReminders();
    } catch {
      setFeedback({ type: 'error', msg: 'Could not add' });
    }
    setSaving(false);
    setTimeout(() => setFeedback(null), 2500);
  };

  const cancelReminder = async (id) => {
    try {
      await fetch(`/api/reminders/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchReminders();
    } catch { /* ignore */ }
  };

  // Migration 0060 — switch a reminder between call mode (scheduler
  // makes a proactive ring) and conversation mode (Berta brings it up
  // naturally during a normal call). Optimistic update on the local
  // list so the click feels instant; re-fetch on failure to resync.
  const setReminderDeliveryMode = async (id, mode) => {
    setReminders(prev => prev.map(r => (r.id === id ? { ...r, delivery_mode: mode } : r)));
    try {
      const res = await fetch(`/api/reminders/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ delivery_mode: mode }),
      });
      if (!res.ok) throw new Error('patch failed');
    } catch {
      await fetchReminders();
    }
  };

  if (loading) return <div className="loading-spinner">{t.loading || 'Loading...'}</div>;

  const pending = reminders.filter(r => r.status === 'pending');
  const failed = reminders.filter(r => r.status === 'delivery_failed');
  const completed = reminders.filter(r => r.status === 'done').slice(0, 5);

  // #200 PR-B — non-engagement banner when voice_engagement is low/inactive.
  // Suppressed for the default 'active' state so brand-new accounts don't
  // see scary copy before any data exists.
  const banner = nonEngagementBanner(voiceEngagement, elderlyName);

  // #204 — disclaimer state. State A locks the form; State B renders
  // the compact note + modal-on-demand. 'loading' renders a placeholder
  // while /me is in flight to avoid flashing State A then State B.
  const disclaimerState = stateForDisclaimer(disclaimerAckAt);
  const formLocked = disclaimerState !== 'acknowledged';

  return (
    <>
      {disclaimerState === 'pending' && (
        <div
          className="detail-card"
          style={{
            marginBottom: 16,
            borderLeft: '4px solid #c0392b',
            background: '#fff3f3',
            padding: '18px 20px',
          }}
        >
          <div style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: 12 }}>
            {DISCLAIMER_TEXT.title}
          </div>
          <div style={{ fontSize: '0.92rem', color: '#444', marginBottom: 12, lineHeight: 1.5 }}>
            {DISCLAIMER_TEXT.intro}
          </div>
          <ul style={{ margin: '0 0 14px 0', paddingLeft: 20, color: '#444', fontSize: '0.88rem', lineHeight: 1.5 }}>
            {DISCLAIMER_TEXT.bullets.map((b, i) => (
              <li key={i} style={{ marginBottom: 8 }}>
                <strong>{b.heading}</strong> {b.body}
              </li>
            ))}
          </ul>
          <div style={{ fontSize: '0.92rem', color: '#444', marginBottom: 16, fontStyle: 'italic' }}>
            {DISCLAIMER_TEXT.closer}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={disclaimerChecked}
              onChange={e => setDisclaimerChecked(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: '#E8845A' }}
            />
            <span style={{ fontSize: '0.95rem', fontWeight: 500 }}>{DISCLAIMER_TEXT.checkboxLabel}</span>
          </label>
          <button
            className="add-interest-btn"
            onClick={acknowledgeDisclaimer}
            disabled={!disclaimerChecked || acknowledging}
          >
            {acknowledging ? 'Saving…' : DISCLAIMER_TEXT.acceptButton + ' →'}
          </button>
        </div>
      )}

      {disclaimerState === 'acknowledged' && (
        <div style={{ marginBottom: 12, fontSize: '0.78rem', color: '#666', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>ⓘ {DISCLAIMER_TEXT.compact}</span>
          <button
            type="button"
            onClick={() => setDisclaimerModalOpen(true)}
            style={{
              background: 'none', border: 'none', color: '#E8845A',
              cursor: 'pointer', padding: 0, fontSize: '0.78rem', textDecoration: 'underline',
            }}
          >
            {DISCLAIMER_TEXT.viewLink}
          </button>
        </div>
      )}

      {disclaimerModalOpen && (
        <div
          onClick={() => setDisclaimerModalOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, padding: '22px 26px',
              maxWidth: 560, maxHeight: '85vh', overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: 12 }}>
              {DISCLAIMER_TEXT.title}
            </div>
            <div style={{ fontSize: '0.92rem', color: '#444', marginBottom: 12, lineHeight: 1.5 }}>
              {DISCLAIMER_TEXT.intro}
            </div>
            <ul style={{ margin: '0 0 14px 0', paddingLeft: 20, color: '#444', fontSize: '0.88rem', lineHeight: 1.5 }}>
              {DISCLAIMER_TEXT.bullets.map((b, i) => (
                <li key={i} style={{ marginBottom: 8 }}>
                  <strong>{b.heading}</strong> {b.body}
                </li>
              ))}
            </ul>
            <div style={{ fontSize: '0.92rem', color: '#444', fontStyle: 'italic', marginBottom: 18 }}>
              {DISCLAIMER_TEXT.closer}
            </div>
            <button
              className="add-interest-btn"
              onClick={() => setDisclaimerModalOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {banner && (
        <div
          className="detail-card"
          style={{
            marginBottom: 16,
            borderLeft: `4px solid ${banner.severity === 'high' ? '#c0392b' : '#d4881e'}`,
            background: banner.severity === 'high' ? '#fff3f3' : '#fff8ed',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: 6 }}>
            {banner.severity === 'high' ? '⚠️ ' : '📞 '}{banner.title}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.5 }}>
            {banner.body}
          </div>
        </div>
      )}

      {/* Add reminder form — gated by #204 disclaimer acknowledgement.
          When formLocked, content is dimmed and pointer-events disabled
          so inputs / buttons can't be activated. The server enforces
          the same rule (#204 — POST /api/reminders returns 403). */}
      <div
        className="detail-card"
        style={{
          opacity: formLocked ? 0.45 : 1,
          pointerEvents: formLocked ? 'none' : 'auto',
          userSelect: formLocked ? 'none' : 'auto',
        }}
        aria-disabled={formLocked}
      >
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>
          {t.addReminder || 'Add a reminder'}
        </div>

        {/* Type toggle */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {['normal', 'important'].map(type => (
            <button
              key={type}
              onClick={() => setReminderType(type)}
              style={{
                padding: '6px 16px',
                borderRadius: 20,
                border: '1.5px solid',
                borderColor: reminderType === type ? '#E8845A' : '#E0DDD8',
                background: reminderType === type ? '#E8845A' : '#fff',
                color: reminderType === type ? '#fff' : '#666',
                fontFamily: 'inherit',
                fontSize: '0.82rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {type === 'important' ? '🔔 Important' : 'Normal'}
            </button>
          ))}
        </div>
        {reminderType === 'important' && (
          <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: 12 }}>
            Important reminders trigger a proactive call at the scheduled time. Retries up to 3 times if unanswered.
          </div>
        )}

        {/* Delivery-mode toggle (mig 0060): Call vs Conversation.
            'call'         → scheduler makes a proactive ring at the
                              scheduled time (existing behaviour).
            'conversation' → no proactive ring; Berta brings it up
                              naturally during the next call on the day
                              (good for birthday-style soft reminders). */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.82rem', color: '#666', minWidth: 80 }}>Notify by</span>
          {[
            { value: 'call', label: '📞 Call' },
            { value: 'conversation', label: '💬 In conversation' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setDeliveryMode(opt.value)}
              style={{
                padding: '5px 12px',
                borderRadius: 16,
                border: '1.5px solid',
                borderColor: deliveryMode === opt.value ? '#E8845A' : '#E0DDD8',
                background: deliveryMode === opt.value ? '#E8845A' : '#fff',
                color: deliveryMode === opt.value ? '#fff' : '#666',
                fontFamily: 'inherit',
                fontSize: '0.78rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: '0.74rem', color: '#999', marginBottom: 12, lineHeight: 1.4 }}>
          {deliveryMode === 'call'
            ? 'We will call them at the scheduled time.'
            : 'No proactive call. The next time you talk with the companion on the day, the companion will weave the reminder into the conversation naturally — once per day at most.'}
        </div>

        <div className="add-interest-row">
          <input
            className="add-interest-input"
            value={task}
            onChange={e => setTask(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addReminder(); } }}
            placeholder={t.reminderPlaceholder || 'e.g. Call Sophie, take pills...'}
            disabled={saving}
          />
          {reminderType === 'normal' && (
            <input
              type="date"
              className="add-interest-input"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              style={{ maxWidth: 160 }}
              disabled={saving}
            />
          )}
          <button className="add-interest-btn" onClick={addReminder} disabled={saving || !task.trim()}>
            {t.addBtn || 'Add'}
          </button>
        </div>

        {/* Important reminder extra fields */}
        {reminderType === 'important' && (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ fontSize: '0.82rem', color: '#666', minWidth: 80 }}>Call time</label>
              <input
                type="time"
                className="add-interest-input"
                value={scheduledTime}
                onChange={e => setScheduledTime(e.target.value)}
                style={{ maxWidth: 120 }}
                disabled={saving}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ fontSize: '0.82rem', color: '#666', minWidth: 80 }}>Repeat</label>
              <select
                className="add-interest-input"
                value={recurrence}
                onChange={e => setRecurrence(e.target.value)}
                style={{ maxWidth: 160 }}
                disabled={saving}
              >
                <option value="once">Once</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="interval">Every N days</option>
              </select>
            </div>
            {recurrence === 'once' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ fontSize: '0.82rem', color: '#666', minWidth: 80 }}>Date</label>
                <input
                  type="date"
                  className="add-interest-input"
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                  style={{ maxWidth: 160 }}
                  disabled={saving}
                />
              </div>
            )}
            {recurrence === 'weekly' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <label style={{ fontSize: '0.82rem', color: '#666', minWidth: 80 }}>Days</label>
                {DAY_LABELS.map((d, i) => (
                  <button
                    key={i}
                    onClick={() => toggleRecurDay(i)}
                    style={{
                      padding: '4px 10px',
                      borderRadius: 14,
                      border: '1.5px solid',
                      borderColor: recurDays.includes(i) ? '#E8845A' : '#E0DDD8',
                      background: recurDays.includes(i) ? '#E8845A' : '#fff',
                      color: recurDays.includes(i) ? '#fff' : '#666',
                      fontFamily: 'inherit',
                      fontSize: '0.78rem',
                      cursor: 'pointer',
                    }}
                  >
                    {d}
                  </button>
                ))}
              </div>
            )}
            {recurrence === 'interval' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ fontSize: '0.82rem', color: '#666', minWidth: 80 }}>Every</label>
                <input
                  type="number"
                  className="add-interest-input"
                  value={recurInterval}
                  onChange={e => setRecurInterval(parseInt(e.target.value, 10) || 2)}
                  min={1}
                  max={90}
                  style={{ maxWidth: 80 }}
                  disabled={saving}
                />
                <span style={{ fontSize: '0.82rem', color: '#666' }}>days</span>
              </div>
            )}
          </div>
        )}

        {/* Birthday sub-form */}
        <div style={{ fontWeight: 600, marginTop: 20, marginBottom: 12, fontSize: '0.9rem' }}>
          {t.addBirthday || 'Add birthday'}
        </div>
        <div className="add-interest-row">
          <select
            className="add-interest-input"
            value={bdayPerson}
            onChange={e => { setBdayPerson(e.target.value); if (e.target.value !== '__other__') setBdayCustomName(''); }}
            disabled={saving}
          >
            <option value="">{t.selectPerson || 'Select person...'}</option>
            {knownPeople.map(p => (
              <option key={`${p.category}-${p.name}`} value={p.name}>{p.name}</option>
            ))}
            <option value="__other__">{t.otherPerson || 'Other person...'}</option>
          </select>
          {bdayPerson === '__other__' && (
            <input
              className="add-interest-input"
              value={bdayCustomName}
              onChange={e => setBdayCustomName(e.target.value)}
              placeholder={t.birthdayName || 'Name'}
              disabled={saving}
            />
          )}
          <input
            className="add-interest-input"
            value={bdayDate}
            onChange={e => setBdayDate(e.target.value)}
            placeholder={t.birthdayDate || 'Datum (MM-DD)'}
            style={{ minWidth: 120 }}
            disabled={saving}
          />
          <button className="add-interest-btn" onClick={addBirthday} disabled={saving || (bdayPerson === '__other__' ? !bdayCustomName.trim() : !bdayPerson) || !bdayDate.trim()}>
            {t.addBtn || 'Add'}
          </button>
        </div>

        {feedback && <p className={`save-feedback ${feedback.type}`}>{feedback.msg}</p>}
      </div>

      {/* Pending reminders */}
      <div className="detail-card" style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>
          {t.pending || 'Pending'}
        </div>
        {pending.length === 0 ? (
          <p className="empty-state">{t.noReminders || 'No reminders yet.'}</p>
        ) : (
          <ul className="family-list">
            {pending.map(r => {
              const meta = formatReminderMeta(r);
              const schedule = formatReminderSchedule(r);
              return (
                <li
                  key={r.id}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {r.reminder_type === 'important' && (
                        <span style={{ fontSize: '0.95rem' }} title="Important — proactive call">🔔</span>
                      )}
                      <span style={{ fontWeight: 500 }}>{r.task}</span>
                      {r.reminder_type === 'important' && (
                        <span style={{ fontSize: '0.7rem', color: '#E8845A', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          Important
                        </span>
                      )}
                      {r.retry_count > 0 && (
                        <span style={{ fontSize: '0.72rem', color: '#c0392b' }}>
                          {r.retry_count}/3 attempts
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.82rem', color: '#666', marginTop: 2 }}>
                      {schedule}
                    </div>
                    {meta.length > 0 && (
                      <div style={{ fontSize: '0.72rem', color: '#9a9085', marginTop: 2 }}>
                        {meta.join(' · ')}
                      </div>
                    )}
                    {/* Migration 0060 — clickable delivery-mode badge. Click toggles
                        between the two modes via PATCH. Defaults to 'call' so legacy
                        rows render as "📞 Call". */}
                    <div style={{ marginTop: 6 }}>
                      <button
                        onClick={() => setReminderDeliveryMode(r.id, r.delivery_mode === 'conversation' ? 'call' : 'conversation')}
                        title={r.delivery_mode === 'conversation'
                          ? 'Click to switch to a proactive call'
                          : 'Click to switch to in-conversation mention only'}
                        style={{
                          padding: '3px 9px',
                          borderRadius: 12,
                          border: '1px solid #E0DDD8',
                          background: '#FAFAF8',
                          color: '#666',
                          fontFamily: 'inherit',
                          fontSize: '0.72rem',
                          cursor: 'pointer',
                        }}
                      >
                        {r.delivery_mode === 'conversation' ? '💬 In conversation' : '📞 Call'}
                      </button>
                    </div>
                  </div>
                  <button className="chip-delete" onClick={() => cancelReminder(r.id)} title="Cancel" style={{ fontSize: '1rem' }}>&#10005;</button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Failed reminders */}
      {failed.length > 0 && (
        <div className="detail-card" style={{ marginTop: 16, borderLeft: '3px solid #c0392b' }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem', color: '#c0392b' }}>
            Undelivered (3 missed calls)
          </div>
          <ul className="family-list">
            {failed.map(r => (
              <li key={r.id} style={{ color: '#c0392b', padding: '6px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>🔔</span>
                  <span style={{ fontWeight: 500 }}>{r.task}</span>
                </div>
                <div style={{ fontSize: '0.78rem', color: '#999', marginTop: 2 }}>
                  {formatReminderSchedule(r)} — check Alerts for details
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Completed reminders */}
      {completed.length > 0 && (
        <div className="detail-card" style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem', color: '#999' }}>
            {t.completed || 'Completed'}
          </div>
          <ul className="family-list">
            {completed.map(r => (
              <li key={r.id} style={{ color: '#999', textDecoration: 'line-through' }}>
                {r.task}
                {r.completed_at && <span style={{ marginLeft: 8, fontSize: '0.75rem' }}>{new Date(r.completed_at).toLocaleDateString()}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* #200 PR-B — Recent companion calls timeline. Only renders
          when there's at least one to show, so a fresh account doesn't
          see an empty section. */}
      {companionCalls.length > 0 && (
        <div className="detail-card" style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: '0.9rem' }}>
            Recent calls
          </div>
          <div style={{ fontSize: '0.78rem', color: '#999', marginBottom: 12 }}>
            How recent companion calls have gone.
          </div>
          <ul className="family-list">
            {companionCalls.map(call => {
              const outcome = formatOutcome(call, 'family');
              const heading = formatCallHeading(call);
              const attempt = formatAttemptLabel(call);
              return (
                <li
                  key={call.conversation_id || call.started_at}
                  style={{ padding: '8px 0', borderBottom: '1px solid #f3ede7' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '1.1rem' }} title={outcome.label}>
                      {outcome.icon}
                    </span>
                    <span style={{ fontWeight: 500 }}>{heading}</span>
                    {attempt && (
                      <span style={{ fontSize: '0.72rem', color: '#999' }}>· {attempt}</span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#666', marginTop: 2, marginLeft: 26 }}>
                    {formatWhen(call.started_at)} · {outcome.copy}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}

const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const INACTIVITY_OPTIONS = [
  { value: null, label: 'Off' },
  { value: 1, label: '1 day' },
  { value: 2, label: '2 days' },
  { value: 3, label: '3 days' },
  { value: 5, label: '5 days' },
  { value: 7, label: '7 days' },
];

const HOUR_OPTIONS = Array.from({ length: 17 }, (_, i) => i + 6); // 06..22

function CallsSection() {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [newSlotStart, setNewSlotStart] = useState(12);
  const [newSlotEnd, setNewSlotEnd] = useState(13);

  const token = localStorage.getItem('am_token');

  useEffect(() => {
    if (!token) return;
    Promise.all([
      fetch('/api/users/call-settings', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : null),
      fetch('/api/users/call-history', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
    ])
      .then(([s, h]) => { if (s) setSettings(s); setHistory(h || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []); // eslint-disable-line

  const save = async (patch) => {
    setSaving(true);
    setFeedback(null);
    try {
      const res = await fetch('/api/users/call-settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error();
      setSettings(prev => ({ ...prev, ...patch }));
      setFeedback({ type: 'success', msg: 'Saved' });
    } catch {
      setFeedback({ type: 'error', msg: 'Could not save' });
    }
    setSaving(false);
    setTimeout(() => setFeedback(null), 2500);
  };

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (!settings) return <div className="detail-card"><p className="empty-state">No linked user found. Complete the intake first.</p></div>;

  return (
    <>
      {/* Inactivity threshold */}
      <div className="detail-card">
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>
          Inactivity check-in call
        </div>
        <p style={{ fontSize: '0.85rem', color: '#777', marginBottom: 16, lineHeight: 1.5 }}>
          The companion will call if no conversation happens for this many days.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {INACTIVITY_OPTIONS.map(opt => (
            <button
              key={String(opt.value)}
              disabled={saving}
              onClick={() => save({ inactivity_call_days: opt.value })}
              style={{
                padding: '8px 18px',
                borderRadius: 10,
                border: '2px solid',
                borderColor: settings.inactivity_call_days === opt.value ? '#E8845A' : '#E0D8D0',
                background: settings.inactivity_call_days === opt.value ? '#fef3e2' : '#fff',
                fontFamily: 'inherit',
                fontSize: '0.85rem',
                fontWeight: settings.inactivity_call_days === opt.value ? 600 : 400,
                color: '#2C2C2C',
                cursor: saving ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Weekly scheduled call */}
      <div className="detail-card" style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>
          Weekly scheduled call
        </div>
        <p style={{ fontSize: '0.85rem', color: '#777', marginBottom: 16, lineHeight: 1.5 }}>
          Pick a day and hour for a regular weekly call, or turn it off.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={settings.weekly_call_day === null || settings.weekly_call_day === undefined ? '' : settings.weekly_call_day}
            disabled={saving}
            onChange={e => {
              const val = e.target.value === '' ? null : Number(e.target.value);
              save({ weekly_call_day: val });
            }}
            style={{
              padding: '8px 14px',
              borderRadius: 10,
              border: '1.5px solid #E0D8D0',
              fontFamily: 'inherit',
              fontSize: '0.85rem',
              color: '#2C2C2C',
              background: '#FAFAF8',
              minWidth: 140,
            }}
          >
            <option value="">Off</option>
            {DAY_NAMES.map((d, i) => <option key={i} value={i}>{d}</option>)}
          </select>

          {settings.weekly_call_day !== null && settings.weekly_call_day !== undefined && (
            <select
              value={settings.weekly_call_hour ?? 10}
              disabled={saving}
              onChange={e => save({ weekly_call_hour: Number(e.target.value) })}
              style={{
                padding: '8px 14px',
                borderRadius: 10,
                border: '1.5px solid #E0D8D0',
                fontFamily: 'inherit',
                fontSize: '0.85rem',
                color: '#2C2C2C',
                background: '#FAFAF8',
                minWidth: 100,
              }}
            >
              {Array.from({ length: 14 }, (_, i) => i + 7).map(h => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          )}
        </div>
        {feedback && <p className={`save-feedback ${feedback.type}`} style={{ marginTop: 10 }}>{feedback.msg}</p>}
      </div>

      {/* Timezone — #660: must be set correctly before the call-window
          hours below mean anything. */}
      <div className="detail-card" style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>
          Timezone
        </div>
        <p style={{ fontSize: '0.85rem', color: '#777', marginBottom: 16, lineHeight: 1.5 }}>
          We use this to greet them at the right time of day and to schedule reminder calls within their day.
          The hours in the &ldquo;call window&rdquo; and &ldquo;weekly call&rdquo; settings below are interpreted in this timezone.
        </p>
        <TimezoneSelect
          value={settings.timezone}
          onChange={v => save({ timezone: v })}
        />
      </div>

      {/* Call window */}
      <div className="detail-card" style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>
          Call window
        </div>
        <p style={{ fontSize: '0.85rem', color: '#777', marginBottom: 16, lineHeight: 1.5 }}>
          Calls will only be placed within this time window.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={settings.call_window_start ?? 9}
            disabled={saving}
            onChange={e => {
              const val = Number(e.target.value);
              save({ call_window_start: val, call_window_end: Math.max(settings.call_window_end ?? 17, val + 1) });
            }}
            style={{
              padding: '8px 14px', borderRadius: 10, border: '1.5px solid #E0D8D0',
              fontFamily: 'inherit', fontSize: '0.85rem', color: '#2C2C2C', background: '#FAFAF8', minWidth: 100,
            }}
          >
            {HOUR_OPTIONS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
          </select>
          <span style={{ color: '#999', fontSize: '0.85rem' }}>to</span>
          <select
            value={settings.call_window_end ?? 17}
            disabled={saving}
            onChange={e => save({ call_window_end: Number(e.target.value) })}
            style={{
              padding: '8px 14px', borderRadius: 10, border: '1.5px solid #E0D8D0',
              fontFamily: 'inherit', fontSize: '0.85rem', color: '#2C2C2C', background: '#FAFAF8', minWidth: 100,
            }}
          >
            {HOUR_OPTIONS.filter(h => h > (settings.call_window_start ?? 9)).map(h => (
              <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
            ))}
          </select>
        </div>
      </div>

      {/* Blocked time slots */}
      <div className="detail-card" style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>
          Blocked time slots
        </div>
        <p style={{ fontSize: '0.85rem', color: '#777', marginBottom: 16, lineHeight: 1.5 }}>
          No calls will be placed during these times.
        </p>
        {(settings.call_blocked_slots || []).length > 0 && (
          <div className="chip-list" style={{ marginBottom: 16 }}>
            {(settings.call_blocked_slots || []).map((slot, i) => (
              <span key={i} className="chip">
                {String(slot.start).padStart(2, '0')}:00–{String(slot.end).padStart(2, '0')}:00
                <button
                  className="chip-delete"
                  disabled={saving}
                  onClick={() => {
                    const updated = (settings.call_blocked_slots || []).filter((_, j) => j !== i);
                    save({ call_blocked_slots: updated });
                  }}
                  title="Remove"
                >&#10005;</button>
              </span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={newSlotStart}
            disabled={saving}
            onChange={e => {
              const val = Number(e.target.value);
              setNewSlotStart(val);
              if (newSlotEnd <= val) setNewSlotEnd(val + 1);
            }}
            style={{
              padding: '8px 14px', borderRadius: 10, border: '1.5px solid #E0D8D0',
              fontFamily: 'inherit', fontSize: '0.85rem', color: '#2C2C2C', background: '#FAFAF8', minWidth: 100,
            }}
          >
            {HOUR_OPTIONS.slice(0, -1).map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
          </select>
          <span style={{ color: '#999', fontSize: '0.85rem' }}>to</span>
          <select
            value={newSlotEnd}
            disabled={saving}
            onChange={e => setNewSlotEnd(Number(e.target.value))}
            style={{
              padding: '8px 14px', borderRadius: 10, border: '1.5px solid #E0D8D0',
              fontFamily: 'inherit', fontSize: '0.85rem', color: '#2C2C2C', background: '#FAFAF8', minWidth: 100,
            }}
          >
            {HOUR_OPTIONS.filter(h => h > newSlotStart).map(h => (
              <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
            ))}
          </select>
          <button
            className="add-interest-btn"
            disabled={saving}
            onClick={() => {
              const updated = [...(settings.call_blocked_slots || []), { start: newSlotStart, end: newSlotEnd }];
              save({ call_blocked_slots: updated });
            }}
          >
            + Add
          </button>
        </div>
      </div>

      {/* Recent calls */}
      <div className="detail-card" style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: '0.9rem' }}>
          Recent calls
        </div>
        {history.length === 0 ? (
          <p className="empty-state">No calls yet.</p>
        ) : (
          <ul className="family-list">
            {history.map(c => (
              <li key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontWeight: 500 }}>{c.trigger_type === 'inactivity' ? 'Inactivity' : 'Weekly'}</span>
                  <span style={{ marginLeft: 10, fontSize: '0.78rem', color: '#999' }}>
                    {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                  </span>
                </div>
                <span style={{
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: c.status === 'answered' ? '#28A745' : c.status === 'missed' ? '#DC3545' : '#E8845A',
                }}>
                  {c.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function GiftIdeasSection({ data, t }) {
  const [gifts, setGifts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const gt = (t.portal && t.portal.sections && t.portal.sections.gifts) || {};
  const elderlyName = (data && data.data && data.data.name) || '';
  const companionName = (data && (data.companion_name || (data.data && data.data.companion_name))) || 'Lena';

  const fetchGifts = (endpoint = '/api/portal/gift-ideas', method = 'GET') => {
    const token = localStorage.getItem('am_token');
    if (!token) return;
    const opts = { method, headers: { Authorization: `Bearer ${token}` } };
    fetch(endpoint, opts)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setGifts(d); setLoading(false); setRefreshing(false); })
      .catch(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => { fetchGifts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    setRefreshing(true);
    fetchGifts('/api/portal/gift-ideas/refresh', 'POST');
  };

  if (loading) return <div className="loading-spinner">Loading…</div>;

  const name = (gifts && gifts.user_name) || elderlyName;
  const suggestions = (gifts && gifts.suggestions) || [];

  return (
    <>
      {name && (
        <p style={{ fontSize: '0.95rem', color: '#666', marginBottom: 20, lineHeight: 1.6 }}>
          Based on what {companionName} has learned about {name}'s interests
        </p>
      )}
      {suggestions.length === 0 ? (
        <div className="detail-card">
          <p className="empty-state">{gt.empty || 'Add interests to get gift ideas'}</p>
        </div>
      ) : (
        <div className="detail-card">
          {suggestions.map((s, i) => (
            <div key={i} style={{
              padding: '14px 0',
              borderBottom: i < suggestions.length - 1 ? '1px solid #F0EDEA' : 'none',
            }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1A1A1A', marginBottom: 4 }}>
                {'\u{1F381}'} {s.title}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#777', lineHeight: 1.5 }}>
                {s.reason}
              </div>
            </div>
          ))}
        </div>
      )}
      <button
        className="add-interest-btn"
        style={{ marginTop: 16 }}
        onClick={handleRefresh}
        disabled={refreshing}
      >
        {refreshing ? 'Refreshing…' : (gt.refresh || 'Refresh suggestions')}
      </button>
    </>
  );
}

const MOOD_EMOJI = { positive: '\u{1F60A}', neutral: '\u{1F610}', negative: '\u{1F614}' };
const MOOD_LABEL_KEY = { positive: 'happy', neutral: 'neutral', negative: 'low' };

function WellbeingSection({ data, t }) {
  const [wb, setWb] = useState(null);
  const [loading, setLoading] = useState(true);
  const wt = (t.portal && t.portal.sections && t.portal.sections.wellbeing) || {};
  const elderlyName = (data && data.data && data.data.name) || '';
  const companionName = (data && (data.companion_name || (data.data && data.data.companion_name))) || 'Lena';

  useEffect(() => {
    const token = localStorage.getItem('am_token');
    if (!token) return;
    fetch('/api/portal/wellbeing', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setWb(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-spinner">{wt.loading || 'Loading…'}</div>;
  if (!wb) return <div className="detail-card"><p className="empty-state">{wt.noData || 'No conversation data available yet.'}</p></div>;

  const moodLabel = (mood) => {
    if (!mood) return wt.noCalls || 'No calls';
    const emoji = MOOD_EMOJI[mood] || '';
    const key = MOOD_LABEL_KEY[mood];
    const label = (wt.moods && wt.moods[key]) || (key === 'happy' ? 'Happy' : key === 'neutral' ? 'Neutral' : 'Low');
    return `${emoji} ${label}`;
  };

  const periodMoodLabel = (mood) => {
    if (!mood) return wt.noCalls || 'No calls';
    const emoji = MOOD_EMOJI[mood] || '';
    const key = MOOD_LABEL_KEY[mood];
    const label = (wt.periodMoods && wt.periodMoods[key]) || (key === 'happy' ? 'Mostly positive' : key === 'neutral' ? 'Mixed' : 'Mostly low');
    return `${emoji} ${label}`;
  };

  const thStyle = {
    textAlign: 'left', padding: '10px 14px', fontSize: '0.82rem', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: '#999',
    borderBottom: '2px solid #F0EDEA',
  };
  const tdStyle = { padding: '12px 14px', fontSize: '0.95rem', color: '#2C2C2C', borderBottom: '1px solid #F0EDEA' };

  return (
    <>
      {elderlyName && (
        <p style={{ fontSize: '0.95rem', color: '#666', marginBottom: 20, lineHeight: 1.6 }}>
          {(wt.intro || 'This is how {name} has been feeling during conversations with {companion}')
            .replace('{name}', elderlyName)
            .replace('{companion}', companionName)}
        </p>
      )}
      <div className="detail-card" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>{wt.period || 'Period'}</th>
              <th style={thStyle}>{wt.mood || 'Mood'}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...tdStyle, fontWeight: 500 }}>{wt.lastCall || 'Last call'}</td>
              <td style={tdStyle}>{wb.last_call ? moodLabel(wb.last_call.mood) : (wt.noCalls || 'No calls')}</td>
            </tr>
            <tr>
              <td style={{ ...tdStyle, fontWeight: 500 }}>{wt.thisWeek || 'This week'}</td>
              <td style={tdStyle}>{periodMoodLabel(wb.this_week)}</td>
            </tr>
            <tr>
              <td style={{ ...tdStyle, fontWeight: 500 }}>{wt.lastWeek || 'Last week'}</td>
              <td style={tdStyle}>{periodMoodLabel(wb.last_week)}</td>
            </tr>
            <tr>
              <td style={{ ...tdStyle, fontWeight: 500, borderBottom: 'none' }}>{wt.lastMonth || 'Last month'}</td>
              <td style={{ ...tdStyle, borderBottom: 'none' }}>{periodMoodLabel(wb.last_month)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="sensitive-note" style={{ marginTop: 14 }}>
        {wt.privacyNote || 'Based on conversation tone only — no details are shared'}
      </p>
    </>
  );
}

// Default voice per language (matches _LANG_VOICE_MAP in voice_providers.py).
// These are AZURE voice IDs — they're only used when the user's existing
// voice_id is Azure-format ("*Neural") AND we need a new default for a
// freshly-changed language. Non-Azure voices (Deepgram aura-2-*,
// ElevenLabs hash IDs) are NOT replaced by these — see #498 fix.
const DEFAULT_VOICE_FOR_LANG = {
  'en': 'en-US-JennyNeural',
  'nl': 'nl-NL-ColetteNeural',
  'de': 'de-CH-LeniNeural',
  'de-DE': 'de-DE-KatjaNeural',
  'fr': 'fr-CH-ArianeNeural',
  'es': 'es-ES-ElviraNeural',
  'it': 'it-IT-ElsaNeural',
  'pt': 'pt-PT-RaquelNeural',
};

// #498 — recognise voice IDs from non-Azure providers so the Portal
// doesn't clobber them with an Azure default. Pre-#498 the form
// assumed any voice_id without "Neural" in the name was junk and
// replaced it on load + on language change. That broke per-user
// Deepgram (#357 / #470) + ElevenLabs (#357 Phase A) overrides
// that admin had configured: a user set up with `aura-2-beatrix-nl`
// would lose it the next time the family opened the settings page.
function isAzureVoice(id) {
  return typeof id === 'string' && id.includes('Neural');
}
function isDeepgramVoice(id) {
  return typeof id === 'string' && id.startsWith('aura-');
}
function isElevenLabsVoice(id) {
  // ElevenLabs voice IDs are 20-character base62 hashes (no dashes,
  // no "Neural" suffix). This heuristic is loose by design — anything
  // that's NOT obviously Azure / Deepgram / empty is treated as
  // ElevenLabs (preserved). False positives only matter if someone
  // typed garbage; that's still preferable to clobbering admin's
  // intentional configuration.
  return typeof id === 'string' && id.length >= 16 && !isAzureVoice(id) && !isDeepgramVoice(id);
}
function isKnownProviderVoice(id) {
  return isAzureVoice(id) || isDeepgramVoice(id) || isElevenLabsVoice(id);
}

const STYLE_OPTIONS = [
  { value: 'smiley', label: 'Smiley', icon: '😊' },
  { value: 'waveform', label: 'Waveform', icon: '📊' },
  { value: 'text', label: 'Text only', icon: '🔤' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'de', label: 'Deutsch (Schweiz)' },
  { value: 'de-DE', label: 'Deutsch (Deutschland)' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
];

// Legacy DB values → canonical code (for users created before language cleanup)
const LANGUAGE_ALIASES = {
  'de-CH': 'de',
  'nl-NL': 'nl',
  'fr-CH': 'fr',
  'es-ES': 'es',
  'it-IT': 'it',
  'pt-PT': 'pt',
  'en-US': 'en',
  'en-GB': 'en',
};

function resolveLanguage(raw) {
  if (!raw) return 'en';
  if (LANGUAGE_OPTIONS.some(o => o.value === raw)) return raw;
  const alias = LANGUAGE_ALIASES[raw];
  if (alias) {
    console.warn(`[Portal] Language "${raw}" mapped to alias "${alias}"`);
    return alias;
  }
  console.warn(`[Portal] Stored language "${raw}" not found in LANGUAGE_OPTIONS — defaulting to "en"`);
  return 'en';
}

const PERSONALITY_PRESETS = {
  nurturer:       { icon: '🌸', warmth: 0.2, humour: 0.4, pace: 0.3, formality: 0.7, proactivity: 0.3 },
  straightTalker: { icon: '🤝', warmth: 0.6, humour: 0.6, pace: 0.7, formality: 0.4, proactivity: 0.7 },
  joker:          { icon: '😄', warmth: 0.1, humour: 0.1, pace: 0.2, formality: 0.8, proactivity: 0.2 },
  intellectual:   { icon: '📚', warmth: 0.5, humour: 0.6, pace: 0.2, formality: 0.3, proactivity: 0.3 },
  listener:       { icon: '🧘', warmth: 0.2, humour: 0.5, pace: 0.6, formality: 0.7, proactivity: 0.9 },
};

const PERSONALITY_DIMS = ['warmth', 'humour', 'pace', 'formality', 'proactivity'];
const DEFAULT_PERSONALITY = { preset: 'nurturer', warmth: 0.2, humour: 0.4, pace: 0.3, formality: 0.7, proactivity: 0.3 };

function personalityPreviewText(p, name, pt) {
  const descs = [];
  if (p.warmth < 0.4) descs.push(pt.warmthLeft || 'warm and nurturing');
  else if (p.warmth > 0.6) descs.push(pt.warmthRight || 'direct');
  else descs.push('balanced');
  if (p.humour < 0.35) descs.push(pt.humourLeft || 'playful');
  else if (p.humour > 0.65) descs.push(pt.humourRight || 'calm and serious');
  if (p.pace < 0.35) descs.push(pt.paceLeft || 'chatty');
  else if (p.pace > 0.65) descs.push(pt.paceRight || 'concise');
  if (p.formality < 0.35) descs.push(pt.formalityLeft || 'casual');
  else if (p.formality > 0.65) descs.push(pt.formalityRight || 'formal and respectful');
  if (p.proactivity < 0.35) descs.push(pt.proactivityLeft || 'takes initiative');
  else if (p.proactivity > 0.65) descs.push(pt.proactivityRight || "follows the user's lead");
  const template = pt.preview || 'With these settings, {name} will be:';
  return `${template.replace('{name}', name)} ${descs.join(', ')}.`;
}

function matchesPreset(p) {
  for (const [key, preset] of Object.entries(PERSONALITY_PRESETS)) {
    if (PERSONALITY_DIMS.every(d => Math.abs((p[d] || 0) - preset[d]) < 0.01)) return key;
  }
  return null;
}

function CompanionSettingsSection({ data, onDataChange }) {
  const { t } = useLang();
  const pt = (t.portal && t.portal.sections && t.portal.sections.personality) || {};
  const d = data.data || {};
  const existingPersonality = data.personality || d.personality || DEFAULT_PERSONALITY;
  const initialLang = resolveLanguage(data.language);
  const [form, setForm] = useState({
    companion_name: data.companion_name || d.companion_name || 'Amigo Mio',
    // #498 — preserve any recognised provider's voice_id (Azure /
    // Deepgram / ElevenLabs); only fall back to the Azure default
    // when there's nothing usable. Pre-#498 this clobbered Deepgram
    // and ElevenLabs IDs because it only recognised "Neural".
    voice_id: isKnownProviderVoice(data.voice_id) ? data.voice_id : (DEFAULT_VOICE_FOR_LANG[initialLang] || 'en-US-JennyNeural'),
    companion_style: data.companion_style || 'smiley',
    language: initialLang,
    speech_rate: typeof data.speech_rate === 'number' ? data.speech_rate : 1.0,
  });
  const [personality, setPersonality] = useState({ ...DEFAULT_PERSONALITY, ...existingPersonality });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [voices, setVoices] = useState([]);
  const [previewingVoice, setPreviewingVoice] = useState(null);

  // Sync form when data prop changes (e.g. after section refetch)
  useEffect(() => {
    const lang = resolveLanguage(data.language);
    // #498 — same provider-aware preservation as the initial state above.
    const vid = isKnownProviderVoice(data.voice_id) ? data.voice_id : (DEFAULT_VOICE_FOR_LANG[lang] || 'en-US-JennyNeural');
    setForm(prev => ({
      ...prev,
      language: lang,
      voice_id: vid,
      companion_name: data.companion_name || (data.data || {}).companion_name || prev.companion_name,
      companion_style: data.companion_style || prev.companion_style,
      speech_rate: typeof data.speech_rate === 'number' ? data.speech_rate : prev.speech_rate,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.language, data.voice_id, data.companion_name, data.companion_style, data.speech_rate]);

  // Fetch voices when language OR provider changes. Pre-fix this only
  // depended on language and never passed `provider`, so /api/voices
  // fell through to the env-default (azure) regardless of which
  // provider admin had configured for the user. Result: a Deepgram
  // user's Companion settings always showed Azure voices in the
  // picker — saving one would clobber their Deepgram voice_id back
  // to an Azure id (or, post-#498, fail the includes('Neural') check
  // and look broken). Pass the user's tts_provider so the picker
  // surfaces the right catalogue.
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('language', form.language);
    if (data.tts_provider) params.set('provider', data.tts_provider);
    fetch(`/api/voices?${params.toString()}`)
      .then(r => r.json())
      .then(list => {
        const items = Array.isArray(list) ? list : [];
        setVoices(items);
        // If the user's currently-selected voice_id isn't in the new
        // list (common case: admin just flipped their provider, or the
        // hidden-via-#557 admin tool removed their previous pick), fall
        // back to the first voice in the list so Save sends a valid id
        // for the current provider rather than a stale cross-provider
        // one. Hands-off when the current id is still valid.
        if (items.length > 0) {
          setForm(prev => {
            const stillValid = items.some(v => v.id === prev.voice_id);
            return stillValid ? prev : { ...prev, voice_id: items[0].id };
          });
        }
      })
      .catch(() => setVoices([]));
  }, [form.language, data.tts_provider]);

  const handleLanguageChange = (lang) => {
    // #498 — only reset voice_id to the language's Azure default if
    // the CURRENT voice is Azure-format. Deepgram + ElevenLabs IDs
    // are admin-configured and shouldn't be replaced when a family
    // user changes the elderly's language in Portal — admin's
    // multi-provider routing takes precedence (the voice will still
    // be wrong-language for the new language, but that's a separate
    // problem solved by #485, and FAR less broken than silently
    // reverting to Azure and breaking the Deepgram routing entirely).
    setForm(prev => {
      const defaultVoice = DEFAULT_VOICE_FOR_LANG[lang] || DEFAULT_VOICE_FOR_LANG[lang.split('-')[0]] || 'en-US-JennyNeural';
      const nextVoice = isAzureVoice(prev.voice_id) ? defaultVoice : prev.voice_id;
      return { ...prev, language: lang, voice_id: nextVoice };
    });
  };

  const playPreview = async (voiceId) => {
    setPreviewingVoice(voiceId);
    try {
      const res = await fetch('/api/voices/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_id: voiceId,
          language: form.language,
          // Same fix as the /api/voices fetch above — without the
          // provider, preview falls through to env-default Azure and
          // synthesizes a Deepgram-IDed voice through the wrong engine
          // (returns either silence or an unrelated Azure default).
          provider: data.tts_provider || undefined,
        }),
      });
      if (!res.ok) throw new Error('Preview failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); setPreviewingVoice(null); };
      audio.onerror = () => { URL.revokeObjectURL(url); setPreviewingVoice(null); };
      audio.play();
    } catch {
      setPreviewingVoice(null);
    }
  };

  const activePreset = matchesPreset(personality);

  const applyPreset = (key) => {
    const p = PERSONALITY_PRESETS[key];
    setPersonality({ preset: key, warmth: p.warmth, humour: p.humour, pace: p.pace, formality: p.formality, proactivity: p.proactivity });
  };

  const setDim = (dim, val) => {
    const v = parseFloat(val);
    const clamped = dim === 'warmth' ? Math.min(v, 0.7) : v;
    setPersonality(prev => ({ ...prev, [dim]: clamped, preset: 'custom' }));
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    const token = localStorage.getItem('am_token');
    try {
      const res = await fetch('/api/intake/review', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          user_id: data.user_id,
          data: {
            companion_name: form.companion_name,
            voice_id: form.voice_id,
            companion_style: form.companion_style,
            language: form.language,
            speech_rate: form.speech_rate,
            personality: personality,
          },
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      setFeedback({ type: 'success', msg: 'Saved' });
      if (onDataChange) {
        onDataChange({
          ...data,
          companion_name: form.companion_name,
          voice_id: form.voice_id,
          companion_style: form.companion_style,
          language: form.language,
          speech_rate: form.speech_rate,
          personality: personality,
        });
      }
    } catch {
      setFeedback({ type: 'error', msg: 'Could not save' });
    }
    setSaving(false);
    setTimeout(() => setFeedback(null), 2500);
  };

  const sliderTrack = { width: '100%', accentColor: '#E8845A' };
  const dimLabels = {
    warmth:      { left: pt.warmthLeft || 'Warm & nurturing', right: pt.warmthRight || 'Direct' },
    humour:      { left: pt.humourLeft || 'Playful & funny', right: pt.humourRight || 'Calm & serious' },
    pace:        { left: pt.paceLeft || 'Chatty & expansive', right: pt.paceRight || 'Concise' },
    formality:   { left: pt.formalityLeft || 'Casual & friendly', right: pt.formalityRight || 'Formal & respectful' },
    proactivity: { left: pt.proactivityLeft || 'Takes initiative', right: pt.proactivityRight || "Follows the user's lead" },
  };

  return (
    <div className="detail-card">
      <div className="detail-row">
        <span className="detail-label">Companion name</span>
        <input
          className="edit-input"
          value={form.companion_name}
          onChange={e => setForm({ ...form, companion_name: e.target.value })}
          placeholder="e.g. Lena, Sofia, Maria…"
        />
      </div>
      <div className="detail-row">
        <span className="detail-label">Voice</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
          {voices.map(v => (
            <div key={v.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', borderRadius: 10,
              border: form.voice_id === v.id ? '2px solid #E8845A' : '1.5px solid #E0D8D0',
              background: form.voice_id === v.id ? '#FFF5F0' : '#FAFAF8',
              cursor: 'pointer',
            }} onClick={() => setForm({ ...form, voice_id: v.id })}>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: form.voice_id === v.id ? 600 : 400, fontSize: '0.9rem' }}>{v.name}</span>
                <span style={{ marginLeft: 8, fontSize: '0.75rem', color: '#888', background: '#F0EDEA', padding: '2px 6px', borderRadius: 4 }}>
                  {v.gender}
                </span>
                {v.region && <span style={{ marginLeft: 4, fontSize: '0.75rem', color: '#888', background: '#F0EDEA', padding: '2px 6px', borderRadius: 4 }}>{v.region}</span>}
              </div>
              <button
                onClick={e => { e.stopPropagation(); playPreview(v.id); }}
                disabled={previewingVoice === v.id}
                style={{
                  padding: '4px 10px', borderRadius: 8, border: '1px solid #E0D8D0',
                  background: previewingVoice === v.id ? '#E8845A' : '#fff',
                  color: previewingVoice === v.id ? '#fff' : '#333',
                  cursor: previewingVoice === v.id ? 'default' : 'pointer',
                  fontSize: '0.8rem', fontFamily: 'inherit',
                }}
              >
                {previewingVoice === v.id ? 'Playing...' : 'Preview'}
              </button>
            </div>
          ))}
          {voices.length === 0 && <span style={{ color: '#999', fontSize: '0.85rem' }}>Loading voices...</span>}
        </div>
      </div>
      <div className="detail-row">
        <span className="detail-label">Face style</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {STYLE_OPTIONS.map(o => (
            <button
              key={o.value}
              onClick={() => setForm({ ...form, companion_style: o.value })}
              style={{
                padding: '8px 14px',
                borderRadius: 10,
                border: form.companion_style === o.value ? '2px solid #E8845A' : '1.5px solid #E0D8D0',
                background: form.companion_style === o.value ? '#FFF5F0' : '#FAFAF8',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontFamily: 'inherit',
                fontWeight: form.companion_style === o.value ? 600 : 400,
                color: '#2C2C2C',
              }}
            >
              {o.icon} {o.label}
            </button>
          ))}
        </div>
      </div>
      <div className="detail-row">
        <span className="detail-label">Language</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
          <select className="edit-select" value={form.language} onChange={e => handleLanguageChange(e.target.value)}>
            {LANGUAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {/* #522 — surface the (deepgram, pt) limitation BEFORE the
              family hits Save and gets the warning back. Only renders
              for users currently routed through Deepgram (admin set
              `tts_provider='deepgram'`); silent for everyone else. */}
          {data.tts_provider === 'deepgram' && form.language === 'pt' && (
            <p style={{ fontSize: '0.78rem', color: '#A66633', margin: 0, lineHeight: 1.35 }}>
              Note: Deepgram doesn't support Portuguese yet — selecting Portuguese will use Azure.
            </p>
          )}
        </div>
      </div>

      {/* #498 — speech rate. Range mirrors the server-side CHECK in
          mig 0056 (0.7-1.3); 1.0 = current default behaviour. Step is
          0.05 so the slider has 12 reachable settings — enough to
          notice a change between adjacent steps without overwhelming
          the family configuring it. */}
      <div className="detail-row">
        <span className="detail-label">Speaking speed</span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%' }}>
          <input
            type="range"
            min="0.7" max="1.3" step="0.05"
            value={form.speech_rate}
            onChange={e => setForm({ ...form, speech_rate: parseFloat(e.target.value) })}
            style={{ width: '100%', accentColor: '#E8845A' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: '#999' }}>
            <span>Slower</span>
            <span>{form.speech_rate.toFixed(2)}×</span>
            <span>Faster</span>
          </div>
        </div>
      </div>

      {/* ── Personality ─────────────────────────────────────────── */}
      <div style={{ marginTop: 28, borderTop: '1px solid #F0EDEA', paddingTop: 20 }}>
        <span className="detail-label" style={{ display: 'block', marginBottom: 12 }}>
          {pt.title || 'Personality'}
        </span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 18 }}>
          {Object.entries(PERSONALITY_PRESETS).map(([key, preset]) => (
            <button
              key={key}
              onClick={() => applyPreset(key)}
              style={{
                padding: '8px 14px',
                borderRadius: 10,
                border: activePreset === key ? '2px solid #E8845A' : '1.5px solid #E0D8D0',
                background: activePreset === key ? '#FFF5F0' : '#FAFAF8',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontFamily: 'inherit',
                fontWeight: activePreset === key ? 600 : 400,
                color: '#2C2C2C',
              }}
            >
              {preset.icon} {pt[key] || key}
            </button>
          ))}
          {activePreset === null && (
            <span style={{
              padding: '8px 14px', borderRadius: 10,
              border: '2px solid #E8845A', background: '#FFF5F0',
              fontSize: '0.85rem', fontWeight: 600, color: '#2C2C2C',
            }}>
              {pt.custom || 'Custom'}
            </span>
          )}
        </div>

        {PERSONALITY_DIMS.map(dim => (
          <div key={dim} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#666', marginBottom: 4 }}>
              <span>{dimLabels[dim].left}</span>
              <span>{dimLabels[dim].right}</span>
            </div>
            <input
              type="range"
              min="0" max={dim === 'warmth' ? '0.7' : '1'} step="0.05"
              value={personality[dim]}
              onChange={e => setDim(dim, e.target.value)}
              style={sliderTrack}
            />
            <div style={{ textAlign: 'center', fontSize: '0.78rem', color: '#999' }}>
              {pt[dim] || dim}: {personality[dim].toFixed(2)}
            </div>
          </div>
        ))}

        <div style={{
          marginTop: 12, padding: '12px 16px', borderRadius: 10,
          background: '#FFF9F5', border: '1px solid #F0EDEA',
          fontSize: '0.88rem', color: '#5A4A3A', lineHeight: 1.5,
        }}>
          {personalityPreviewText(personality, form.companion_name, pt)}
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <button className="btn-save-section" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
      {feedback && <p className={`save-feedback ${feedback.type}`}>{feedback.msg}</p>}
    </div>
  );
}

// #276 — per-feed filter editor. Expandable "Filters" panel under each
// RSS connection. Edits are saved via PATCH /api/portal/social-connections/:id/filters.
function FeedFilters({ connection, onSaved }) {
  const [open, setOpen] = useState(false);
  const [minSig, setMinSig] = useState(connection.min_significance ?? 0);
  const [includeKw, setIncludeKw] = useState((connection.include_keywords || []).join(', '));
  const [excludeKw, setExcludeKw] = useState((connection.exclude_keywords || []).join(', '));
  const [allowedCats, setAllowedCats] = useState(new Set(connection.allowed_categories || []));
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const ALL_CATEGORIES = [
    { key: 'life_event', label: 'Life events (births, marriages, milestones)' },
    { key: 'photo',      label: 'Photos of family / friends' },
    { key: 'opinion',    label: 'Opinions and commentary' },
    { key: 'promotion',  label: 'Promotions / ads' },
    { key: 'other',      label: 'Other' },
  ];

  const toggleCat = (key) => {
    setAllowedCats(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setSavedFlash(false);
    const token = localStorage.getItem('am_token');
    const body = {
      min_significance: Number(minSig),
      include_keywords: includeKw.split(',').map(s => s.trim()).filter(Boolean),
      exclude_keywords: excludeKw.split(',').map(s => s.trim()).filter(Boolean),
      // Empty array is sent if user has not picked any — backend
      // interprets that as "no filter" (NULL).
      allowed_categories: allowedCats.size === 0 ? [] : Array.from(allowedCats),
    };
    try {
      const res = await fetch(`/api/portal/social-connections/${connection.id}/filters`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 2000);
        if (onSaved) onSaved();
      }
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0ede8' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', color: '#3a8fb7', cursor: 'pointer', padding: 0, fontSize: '0.85rem' }}
      >
        {open ? '▼' : '▶'} Filters {open ? '' : '(expand to tune what Lena surfaces)'}
      </button>
      {open && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>
              Minimum significance: <strong>{minSig}</strong> / 10
            </label>
            <input
              type="range"
              min={0}
              max={10}
              value={minSig}
              onChange={e => setMinSig(e.target.value)}
              style={{ width: '100%', marginTop: 4 }}
            />
            <div style={{ fontSize: '0.75rem', color: '#888' }}>
              Higher = only meaningful posts (life events, photos). 0 = no filter.
            </div>
          </div>
          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Include keywords (comma-separated)</label>
            <input
              type="text"
              className="input-field"
              placeholder="e.g. wedding, baby, anniversary"
              value={includeKw}
              onChange={e => setIncludeKw(e.target.value)}
              style={{ marginTop: 4 }}
            />
            <div style={{ fontSize: '0.75rem', color: '#888' }}>
              If set, only items matching at least one keyword are surfaced.
            </div>
          </div>
          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Exclude keywords (comma-separated)</label>
            <input
              type="text"
              className="input-field"
              placeholder="e.g. sale, ad, sponsored"
              value={excludeKw}
              onChange={e => setExcludeKw(e.target.value)}
              style={{ marginTop: 4 }}
            />
            <div style={{ fontSize: '0.75rem', color: '#888' }}>
              Items matching any of these are filtered out.
            </div>
          </div>
          <div>
            <label style={{ fontSize: '0.85rem', fontWeight: 500 }}>Categories (none ticked = all allowed)</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
              {ALL_CATEGORIES.map(c => (
                <label key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
                  <input
                    type="checkbox"
                    checked={allowedCats.has(c.key)}
                    onChange={() => toggleCat(c.key)}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="btn-save" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save filters'}
            </button>
            {savedFlash && <span style={{ fontSize: '0.85rem', color: '#4caf50' }}>Saved.</span>}
          </div>
        </div>
      )}
    </div>
  );
}


function SocialSection() {
  const { t } = useLang();
  const social = t?.portal?.sections?.social || {};
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rssForm, setRssForm] = useState({ person_name: '', relationship: 'rss', feed_url: '' });
  const [manualForm, setManualForm] = useState({ person_name: '', relationship: '', content: '' });
  const [youtubeForm, setYoutubeForm] = useState({ person_name: '', feed_url: '' });
  const [saving, setSaving] = useState(false);

  const fetchConnections = () => {
    const token = localStorage.getItem('am_token');
    if (!token) return;
    fetch('/api/portal/social-connections', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setConnections(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchConnections(); }, []);

  const addRss = async (e) => {
    e.preventDefault();
    if (!rssForm.feed_url) return;
    setSaving(true);
    const token = localStorage.getItem('am_token');
    try {
      const res = await fetch('/api/portal/social-connections', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ feed_type: 'rss', ...rssForm }),
      });
      if (res.ok) {
        setRssForm({ person_name: '', relationship: 'rss', feed_url: '' });
        fetchConnections();
      }
    } catch { /* ignore */ }
    setSaving(false);
  };

  const addManual = async (e) => {
    e.preventDefault();
    if (!manualForm.content) return;
    setSaving(true);
    const token = localStorage.getItem('am_token');
    try {
      const res = await fetch('/api/portal/social-connections', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ feed_type: 'manual', ...manualForm }),
      });
      if (res.ok) {
        setManualForm({ person_name: '', relationship: '', content: '' });
        fetchConnections();
      }
    } catch { /* ignore */ }
    setSaving(false);
  };

  const addYoutube = async (e) => {
    e.preventDefault();
    if (!youtubeForm.feed_url) return;
    setSaving(true);
    const token = localStorage.getItem('am_token');
    try {
      const res = await fetch('/api/portal/social-connections', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ feed_type: 'youtube', person_name: youtubeForm.person_name, feed_url: youtubeForm.feed_url }),
      });
      if (res.ok) {
        setYoutubeForm({ person_name: '', feed_url: '' });
        fetchConnections();
      }
    } catch { /* ignore */ }
    setSaving(false);
  };

  const removeConnection = async (id) => {
    const token = localStorage.getItem('am_token');
    try {
      const res = await fetch(`/api/portal/social-connections/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setConnections(prev => prev.filter(c => c.id !== id));
    } catch { /* ignore */ }
  };

  if (loading) return <div className="loading-spinner">Loading...</div>;

  return (
    <>
      {/* Section 1: Family updates */}
      <div className="detail-card" style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 4 }}>👨‍👩‍👧 {social.family_updates || 'Family updates'}</h3>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: 12 }}>{social.family_updates_desc || 'Share something that happened in the family — Lena will mention it in conversation.'}</p>
        <form onSubmit={addManual} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            className="input-field"
            placeholder={social.person_name_placeholder || 'Who is this about? (e.g. Carla)'}
            value={manualForm.person_name}
            onChange={e => setManualForm(f => ({ ...f, person_name: e.target.value }))}
          />
          <input
            className="input-field"
            placeholder={social.relationship_placeholder || 'Their relationship (e.g. daughter, grandson)'}
            value={manualForm.relationship}
            onChange={e => setManualForm(f => ({ ...f, relationship: e.target.value }))}
          />
          <textarea
            className="input-field"
            placeholder={social.what_happened_placeholder || 'What happened? Write an update...'}
            value={manualForm.content}
            onChange={e => setManualForm(f => ({ ...f, content: e.target.value }))}
            rows={3}
            required
            style={{ resize: 'vertical' }}
          />
          <button className="btn-save" type="submit" disabled={saving}>
            {saving ? '...' : (social.share_update || 'Share update')}
          </button>
        </form>
      </div>

      {/* Section 2: YouTube channels */}
      <div className="detail-card" style={{ marginBottom: 24 }}>
        <h3 style={{ marginBottom: 4 }}>📺 {social.youtube_channels || 'YouTube channels'}</h3>
        <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: 12 }}>{social.youtube_desc || 'Add a YouTube channel — Lena can mention new videos.'}</p>
        <form onSubmit={addYoutube} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            className="input-field"
            placeholder={social.channel_name_placeholder || "Channel name (e.g. Carla's cooking channel)"}
            value={youtubeForm.person_name}
            onChange={e => setYoutubeForm(f => ({ ...f, person_name: e.target.value }))}
          />
          <input
            className="input-field"
            placeholder={social.channel_url_placeholder || 'YouTube URL or handle (e.g. @carla)'}
            value={youtubeForm.feed_url}
            onChange={e => setYoutubeForm(f => ({ ...f, feed_url: e.target.value }))}
            required
          />
          <button className="btn-save" type="submit" disabled={saving}>
            {saving ? '...' : (social.add_channel || 'Add channel')}
          </button>
        </form>
      </div>

      {/* Section 3: RSS feeds (collapsed) */}
      <details style={{ marginBottom: 24 }}>
        <summary style={{ cursor: 'pointer', padding: '12px 16px', background: '#f5f5f5', borderRadius: 8, fontSize: '1rem', fontWeight: 600 }}>
          🔗 {social.rss_advanced || 'Advanced: RSS feed'}
        </summary>
        <div style={{ padding: '0 0 8px 0' }}>
          <div className="detail-card" style={{ marginTop: 12 }}>
            <form onSubmit={addRss} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input
                className="input-field"
                placeholder={social.person_name_placeholder || 'Person name (e.g. Giulia)'}
                value={rssForm.person_name}
                onChange={e => setRssForm(f => ({ ...f, person_name: e.target.value }))}
              />
              <input
                className="input-field"
                placeholder="RSS feed URL"
                value={rssForm.feed_url}
                onChange={e => setRssForm(f => ({ ...f, feed_url: e.target.value }))}
                required
              />
              <button className="btn-save" type="submit" disabled={saving}>
                {saving ? '...' : 'Add feed'}
              </button>
            </form>
          </div>
        </div>
      </details>

      {/* Connections list */}
      {connections.length === 0 ? (
        <div className="detail-card">
          <p className="empty-state">{social.family_updates_desc || 'Add family updates, YouTube channels, or RSS feeds for the companion to mention in conversation.'}</p>
        </div>
      ) : (
        connections.map(c => (
          <div key={c.id} className="detail-card" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span style={{
                  display: 'inline-block',
                  fontSize: '0.75rem',
                  padding: '2px 8px',
                  borderRadius: 4,
                  background: c.feed_type === 'manual' ? '#e8f5e9' : c.feed_type === 'youtube' ? '#fce4ec' : '#e3f2fd',
                  marginBottom: 4,
                }}>
                  {c.feed_type === 'manual' ? '👨‍👩‍👧 Family update' : c.feed_type === 'youtube' ? '📺 YouTube' : '🔗 RSS'}
                </span>
                <div><strong>{c.person_name || 'Unknown'}</strong>
                {c.relationship && <span style={{ color: '#888', marginLeft: 8 }}>({c.relationship})</span>}</div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: 4 }}>
                  {c.feed_type === 'manual' ? (social.family_updates || 'Family update') : c.feed_url}
                </div>
              </div>
              <button
                onClick={() => removeConnection(c.id)}
                style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer', fontSize: '1.1rem' }}
              >
                Remove
              </button>
            </div>
            {/* #276 — filters panel for RSS feeds. Manual updates have only one item, no filtering needed. */}
            {c.feed_type === 'rss' && (
              <FeedFilters connection={c} onSaved={fetchConnections} />
            )}
          </div>
        ))
      )}
    </>
  );
}

export default function Portal() {
  const { t } = useLang();
  const p = t.portal;
  const pd = p.detail || {};
  const navigate = useNavigate();
  const location = useLocation();
  // Family Portal — coral favicon. See utils/useFavicon for palette.
  useFavicon('portal');
  const [user, setUser] = useState(null);
  const [intakeComplete, setIntakeComplete] = useState(null);
  const [intakeData, setIntakeData] = useState(null);
  const [intakeError, setIntakeError] = useState(null);
  // #786 — the per-tile RecentlyNoticedPanel was removed (Story C
  // philosophy correction: extracted conversation facts now surface
  // only in the admin Memory sub-page, never on the family Portal).
  // The intakeReloadKey state that used to trigger a parent re-fetch
  // after a family-confirm goes away with it.
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [setupUrl, setSetupUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [alertCount, setAlertCount] = useState(0);

  const subPath = location.pathname.replace(/^\/portal\/?/, '');
  const activeSection = ['profile', 'interests', 'family', 'friends', 'pets', 'faith', 'childhood', 'work', 'health', 'alerts', 'photos', 'reminders', 'calls', 'wellbeing', 'companion', 'social', 'gifts', 'account'].includes(subPath) ? subPath : null;

  useEffect(() => {
    const token = localStorage.getItem('am_token');
    const raw = localStorage.getItem('am_user');
    if (!token) { navigate('/login'); return; }
    try { setUser(JSON.parse(raw)); } catch { setUser({}); }

    // #244-C — fetch fresh /me so the trial banner has trial_ends_at +
    // subscription_status (login response doesn't include those for
    // back-compat). Quietly merges into user state.
    fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(fresh => {
        if (fresh) {
          setUser(prev => ({ ...(prev || {}), ...fresh }));
          localStorage.setItem('am_user', JSON.stringify(fresh));
        }
      })
      .catch(() => {});

    fetch('/api/intake/review', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        // #243 — accounts in 'not_started' state (fresh signups OR
        // post-factory-reset) should land on the intake form, not the
        // Portal. The form-first GET endpoint returns 200 with
        // intake_status:'not_started' for these (it used to 404 pre-#222),
        // so a truthy r.ok no longer means "intake done".
        if (data && data.intake_status === 'not_started') {
          navigate('/intake/review', { replace: true });
          return;
        }
        setIntakeComplete(Boolean(data));
        if (data) {
          fetch('/api/elderly/setup-token', { headers: { Authorization: `Bearer ${token}` } })
            .then(r2 => r2.ok ? r2.json() : null)
            .then(d => { if (d) setSetupUrl(d.setup_url); });
        }
      })
      .catch(() => { setIntakeComplete(false); });

    // Fetch unread alert count — combines safeguarding + system alerts
    // (#270). Reminder alerts have their own acknowledgement flow and
    // aren't counted here.
    Promise.all([
      fetch('/api/alerts', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
      fetch('/api/system-alerts', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.ok ? r.json() : []),
    ]).then(([safe, sys]) => {
      const safeUnread = (safe || []).filter(a => a.status === 'new').length;
      const sysUnread = (sys || []).filter(a => a.status === 'new').length;
      setAlertCount(safeUnread + sysUnread);
    }).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    if (!activeSection) return;
    const token = localStorage.getItem('am_token');
    if (!token) return;
    setLoading(true);
    setIntakeError(null);
    fetch('/api/intake/review', {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(res => {
        if (res.status === 404) throw new Error('no_intake');
        if (!res.ok) throw new Error('fetch_error');
        return res.json();
      })
      .then(data => { setIntakeData(data); setLoading(false); })
      .catch(err => { setIntakeError(err.message); setLoading(false); });
  }, [activeSection]);

  function logout() {
    localStorage.removeItem('am_token');
    localStorage.removeItem('am_user');
    navigate('/login');
  }

  if (!user) return null;

  const sectionTitles = {
    profile: p.sections.profile.title,
    interests: p.sections.interests.title,
    family: p.sections.family.title,
    friends: (p.sections.friends && p.sections.friends.title) || 'Friends',
    // #711 — fallback titles let the four new tiles render before the
    // i18n bundle catches up (translations.js follow-up).
    pets: (p.sections.pets && p.sections.pets.title) || 'Pets',
    faith: (p.sections.faith && p.sections.faith.title) || 'Faith',
    childhood: (p.sections.childhood && p.sections.childhood.title) || 'Childhood',
    work: (p.sections.work && p.sections.work.title) || 'Working life',
    health: p.sections.health.title,
    alerts: (p.sections.alerts && p.sections.alerts.title) || 'Alerts',
    photos: p.sections.photos.title,
    reminders: (p.sections.reminders && p.sections.reminders.title) || 'Reminders',
    calls: (p.sections.calls && p.sections.calls.title) || 'Proactive Calls',
    wellbeing: (p.sections.wellbeing && p.sections.wellbeing.title) || 'Wellbeing',
    companion: (p.sections.companion && p.sections.companion.title) || 'Companion Settings',
    social: (p.sections.social && p.sections.social.title) || 'Family Updates',
    account: (p.sections.account && p.sections.account.title) || 'Account',
  };

  return (
    <>
      <style>{styles}</style>
      {/* #287 — backfill terms acceptance for accounts created before
          the disclaimer system shipped. The signup flow already writes
          terms_v1 for new accounts; this gate fires only for existing
          users on their next visit. New users pass through immediately. */}
      <DisclaimerGate
        slug="terms_v1"
        title="Welcome back — please review the AmigoMio Terms"
        body={
          "We've added a clear summary of how AmigoMio works and what it's for. " +
          "AmigoMio is a companion app, not a medical or emergency service. " +
          "Notifications and alerts are best-effort — please don't rely on them for " +
          "time-critical events. By clicking I understand you confirm you've read this."
        }
      >
        <header className="portal-header">
          <span className="portal-logo">
            <img src="/logo/logo-mark-watercolor.png" alt="" aria-hidden="true" />
            Amigo Mio
            <span className="ver">v{VERSION}</span>
          </span>
          <div className="portal-user">
            <span className="portal-username">{p.welcome}, {user.name}</span>
            <button className="btn-logout" onClick={logout}>{p.logout}</button>
          </div>
        </header>
        {/* #244-C — trial banner under the header. Hidden once subscription_status flips to 'active'. */}
        <TrialBanner user={user} companionName={intakeData?.data?.companion_name} />
        <main className="portal-main">
        {activeSection ? (
          <>
            <Link className="detail-back" to="/portal">← {pd.backToDashboard || 'Back to dashboard'}</Link>
            <h1 className="detail-title">{sectionTitles[activeSection]}</h1>
            {loading && <div className="loading-spinner">{pd.loading || 'Loading…'}</div>}
            {intakeError === 'no_intake' && (
              <div className="no-intake-banner">
                <h3>{pd.noIntakeTitle || 'No profile yet'}</h3>
                <p>Fill in the basics first — the companion needs that to talk with them.</p>
                {/* #222 form-first: route to the form, not voice intake. */}
                <button className="btn-intake" onClick={() => navigate('/intake/review')}>{p.startIntake}</button>
              </div>
            )}
            {intakeError && intakeError !== 'no_intake' && (
              <div className="no-intake-banner">
                <p>{pd.fetchError || 'Could not load data. Please try again.'}</p>
              </div>
            )}
            {intakeData && !loading && !intakeError && (
              <>
                {activeSection === 'profile' && <ProfileSection data={intakeData} t={pd} onDataChange={setIntakeData} />}
                {/* #786 — the per-tile Recently noticed panels were
                    removed (Story C philosophy correction). The family
                    portal MUST NOT surface what the elderly user said
                    in conversation — that's surveillance. Tentative
                    extracted facts now surface only in the admin
                    Memory sub-page (/admin/users/<id>/memory). The
                    family sees the canonical sections only. */}
                {activeSection === 'interests' && (
                  <InterestsSection data={intakeData} t={pd} onDataChange={setIntakeData} />
                )}
                {activeSection === 'family' && (
                  <FamilySection data={intakeData} t={pd} onDataChange={setIntakeData} />
                )}
                {activeSection === 'friends' && (
                  <FriendsSection data={intakeData} t={pd} />
                )}
                {/* #711 — Pets / Faith / Childhood / Working life share a
                    lightweight read-only summary view that surfaces what
                    the family captured during intake + an "Edit in
                    Intake Review" link routing to /intake/review where
                    the full editor lives. The Portal-side tile satisfies
                    the visibility AC without duplicating the editor
                    logic; the gap-fix fields (places_lived /
                    blocked_topics / joyful_memories) are also editable
                    from the same /intake/review path. */}
                {activeSection === 'pets' && (
                  <IntakeFieldSummarySection
                    data={intakeData}
                    onDataChange={setIntakeData}
                    label="Pets"
                    icon="🐾"
                    blurb="Pets your loved one has now or has had — Lena will ask about them by name."
                    valueRenderer={(d) => (Array.isArray(d?.data?.pets) ? d.data.pets : []).filter(p => p && p.name)}
                    renderItem={(pet, i) => (
                      <li key={i}>
                        <strong>{pet.name}</strong>
                        {pet.species && <> · {pet.species}</>}
                        {pet.status && <> · <em>{pet.status}</em></>}
                        {pet.note && <> · {pet.note}</>}
                      </li>
                    )}
                    emptyState="No pets added yet."
                    field="pets"
                    Editor={({ value, onChange }) => (
                      <PetsEditor pets={Array.isArray(value) ? value : []} onChange={onChange} />
                    )}
                    defaultEditValue={[]}
                  />
                )}
                {activeSection === 'faith' && (
                  <IntakeFieldSummarySection
                    data={intakeData}
                    onDataChange={setIntakeData}
                    label="Faith / spirituality"
                    icon="🕊️"
                    blurb="Tradition, practices, and any holidays Lena should be aware of."
                    valueRenderer={(d) => {
                      // #711 — faith is structured: {tradition, importance, practices}.
                      // Defensively handle the legacy freetext string too.
                      const f = d?.data?.faith;
                      if (!f) return [];
                      if (typeof f === 'string') return f.trim() ? [{ label: '', text: f }] : [];
                      const items = [];
                      if (f.tradition)  items.push({ label: 'Tradition',  text: f.tradition });
                      if (f.importance) items.push({ label: 'Importance', text: f.importance });
                      if (f.practices)  items.push({ label: 'Practices',  text: f.practices });
                      return items;
                    }}
                    renderItem={(item, i) => (
                      <li key={i} style={{whiteSpace:'pre-wrap'}}>
                        {item.label && <strong>{item.label}: </strong>}{item.text}
                      </li>
                    )}
                    emptyState="Nothing captured. Lena will make no faith references."
                    field="faith"
                    Editor={({ value, onChange }) => (
                      <FaithEditor value={value} onChange={onChange} datalistId="portal-tradition-suggestions" />
                    )}
                    defaultEditValue={{ tradition: '', importance: '', practices: '' }}
                  />
                )}
                {activeSection === 'childhood' && (
                  <IntakeFieldSummarySection
                    data={intakeData}
                    onDataChange={setIntakeData}
                    label="Childhood"
                    icon="📖"
                    blurb="Where they grew up, family of origin, formative memories."
                    valueRenderer={(d) => d?.data?.childhood ? [d.data.childhood] : []}
                    renderItem={(v, i) => <li key={i} style={{whiteSpace:'pre-wrap'}}>{v}</li>}
                    emptyState="Nothing captured."
                    extraNote={(d) => d?.data?.childhood_sensitive_notes ? (
                      <div style={{ marginTop: 12, padding: '10px 14px', background: '#FBEFEF', border: '1px dashed #E5C5C3', borderRadius: 6, fontSize: '0.85rem', color: '#B85450' }}>
                        🔒 Sensitive notes saved (private — never shown back).
                      </div>
                    ) : null}
                    field="childhood"
                    Editor={ChildhoodEditor}
                    defaultEditValue={{ childhood: '', childhood_sensitive_notes: '' }}
                    draftInitializer={(d) => ({
                      childhood: (d?.data?.childhood) || '',
                      // Sensitive notes are write-only by design — the
                      // server intentionally does NOT round-trip the
                      // text in extracted_data, so the editor opens
                      // with the field blank. Typing in it OVERWRITES
                      // the saved sensitive notes; leaving it blank
                      // CLEARS them (consistent with how the Intake
                      // form behaves).
                      childhood_sensitive_notes: '',
                    })}
                    saveTransform={(draft) => ({
                      childhood: draft?.childhood || '',
                      childhood_sensitive_notes: draft?.childhood_sensitive_notes || '',
                    })}
                    optimisticPatch={(draft) => ({
                      childhood: draft?.childhood || '',
                      // Don't echo sensitive notes back into local
                      // data even after save — keeps the view-mode
                      // UI honest about the write-only contract.
                      childhood_sensitive_notes: draft?.childhood_sensitive_notes
                        ? '__present__' : '',
                    })}
                  />
                )}
                {activeSection === 'work' && (
                  <IntakeFieldSummarySection
                    data={intakeData}
                    onDataChange={setIntakeData}
                    label="Working life"
                    icon="💼"
                    blurb="What they did, where, what they were proud of."
                    valueRenderer={(d) => {
                      // #711 — career is structured: {profession_or_role,
                      // employer, industry, years_active, source_of_pride}.
                      // Defensively handle the legacy freetext string too.
                      const c = d?.data?.career;
                      if (!c) return [];
                      if (typeof c === 'string') return c.trim() ? [{ label: '', text: c }] : [];
                      const items = [];
                      if (c.profession_or_role) items.push({ label: 'Profession',  text: c.profession_or_role });
                      if (c.employer)           items.push({ label: 'Employer',    text: c.employer });
                      if (c.industry)           items.push({ label: 'Industry',    text: c.industry });
                      if (c.years_active)       items.push({ label: 'Years',       text: c.years_active });
                      if (c.source_of_pride)    items.push({ label: 'Most proud of', text: c.source_of_pride });
                      return items;
                    }}
                    renderItem={(item, i) => (
                      <li key={i} style={{whiteSpace:'pre-wrap'}}>
                        {item.label && <strong>{item.label}: </strong>}{item.text}
                      </li>
                    )}
                    emptyState="Nothing captured."
                    field="career"
                    Editor={({ value, onChange }) => (
                      <WorkingLifeEditor value={value} onChange={onChange} />
                    )}
                    defaultEditValue={{
                      profession_or_role: '',
                      employer: '',
                      industry: '',
                      years_active: '',
                      source_of_pride: '',
                    }}
                  />
                )}
                {activeSection === 'health' && <HealthSection data={intakeData} t={pd} />}
                {activeSection === 'companion' && <CompanionSettingsSection data={intakeData} onDataChange={setIntakeData} />}
                {activeSection === 'account' && <AccountSection />}
              </>
            )}
            {activeSection === 'alerts' && (
              <DisclaimerGate
                slug="alerts_v1"
                title="Alerts are a help, not a solution"
                body={
                  "AmigoMio is not a medical or emergency service. " +
                  "The alerts shown here are best-effort notifications — they are not guaranteed to reach you, " +
                  "be timely, or capture every event. For anything urgent, please contact your loved one directly " +
                  "or call your local emergency services."
                }
              >
                <AlertsSection />
              </DisclaimerGate>
            )}
            {activeSection === 'photos' && <PhotosSection />}
            {activeSection === 'reminders' && <RemindersSection t={pd} />}
            {activeSection === 'calls' && <CallsSection />}
            {activeSection === 'gifts' && <GiftIdeasSection data={intakeData} t={t} />}
            {activeSection === 'wellbeing' && <WellbeingSection data={intakeData} t={t} />}
            {activeSection === 'social' && <SocialSection />}
          </>
        ) : (
          <>
            {intakeComplete ? (
              <div className="setup-card">
                <div className="setup-badge">&#10004; Setup complete</div>
                <button
                  className={`btn-copy-setup${copied ? ' copied' : ''}`}
                  onClick={() => {
                    navigator.clipboard.writeText(setupUrl || TABLET_URL);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                >
                  {copied ? '✓ Copied!' : '📋 Copy setup link'}
                </button>
                {showQR ? (
                  <>
                    <div className="setup-qr">
                      <QRCodeSVG value={setupUrl || TABLET_URL} size={180} />
                    </div>
                    <button onClick={() => setShowQR(false)} style={{background:'none',border:'none',color:'#E8845A',fontSize:'0.85rem',fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Hide QR code</button>
                  </>
                ) : (
                  <button onClick={() => setShowQR(true)} style={{background:'none',border:'none',color:'#E8845A',fontSize:'0.85rem',fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Show QR code</button>
                )}
                <p className="setup-hint">Lost your tablet? Just scan this code again to set it up.</p>
              </div>
            ) : intakeComplete === false ? (
              <div className="intake-banner">
                <div className="intake-text">
                  <h2>Tell us about your loved one</h2>
                  <p>Fill in their basics so the companion knows how to talk with them — about 5 minutes.</p>
                </div>
                {/* #222 form-first: route to the form, not the voice
                    intake. The voice intake entry stays accessible at
                    /intake until PR-3 deprecates it. */}
                <button className="btn-intake" onClick={() => navigate('/intake/review')}>{p.startIntake}</button>
              </div>
            ) : null}

            {/* #300 — Portal cockpit: at-a-glance stats above the
                section-cards grid. v1 ships the talk-time gauge.
                Future cards (call-volume, mood, missed reminders)
                land here too. */}
            <PortalCockpit />

            <p className="sections-heading">Your dashboard</p>
            <div className="sections-grid">
              {SECTIONS.map(({ key, icon, active, path }) => {
                const info = p.sections[key] || { title: key, description: '' };
                const cardClass = `section-card ${active ? 'active' : 'inactive'}`;
                const inner = (
                  <>
                    <span className="card-icon">{icon}</span>
                    <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {info.title}
                      {key === 'alerts' && alertCount > 0 && (
                        <span className="alert-badge">{alertCount}</span>
                      )}
                    </div>
                    <div className="card-desc">{info.description}</div>
                    {!active && <span className="coming-soon-badge">{p.comingSoon}</span>}
                  </>
                );
                return active && path
                  ? <Link key={key} className={cardClass} to={path}>{inner}</Link>
                  : <div key={key} className={cardClass}>{inner}</div>;
              })}
            </div>
          </>
        )}
        </main>
      </DisclaimerGate>
    </>
  );
}
